import React, { memo, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import { useTenantView } from '../TenantViewContext';
import { getNodePulseLevel, isNodeWiredToLunaCore } from '../pulseUtils';
import {
  Smartphone,
  Wifi,
  WifiOff,
  Loader2,
  QrCode,
  Phone,
  Shield,
  Lock,
  Unlock,
  RefreshCw,
  Plug,
  AlertCircle,
  CheckCircle2,
  XCircle,
  LogOut,
  Sparkles,
  Activity,
  ArrowRight,
  Bot,
  Zap,
  ShieldCheck,
  Building2,
  Key,
  Link2,
  Copy,
  Check,
  Eye,
  EyeOff,
  Globe,
  PhoneCall,
  PhoneForwarded,
  PhoneIncoming,
  Volume2,
  Mic,
  Server,
  Radio,
  ChevronDown,
  ChevronUp,
  Sliders,
  PowerOff,
  Trash2,
  Clock,
} from 'lucide-react';
import { KangavenaWhatsAppSimulatorModal } from '../../tenet/KangavenaWhatsAppSimulatorModal';

export interface WhatsAppInstanceNodeData {
  apiMode?: 'unofficial' | 'official';
  // --- Official Meta Cloud API Credentials ---
  metaAccessToken?: string;
  metaPhoneNumberId?: string;
  metaWabaId?: string;
  metaVerifyToken?: string;
  // --- Meta SIP Real-Time Calling (Phase 41) ---
  enableSipCalling?: boolean;
  sipServerHost?: string;
  sipPort?: number;
  sipAppId?: string;
  sipAction?: 'gemini_live_answer' | 'luna_live_answer' | 'voice_note_reply' | 'live_web_handover';
  sipCustomParamName?: string;
  sipCustomParamValue?: string;
  // --- Unofficial Evolution API Fields (preserved) ---
  connectionStatus?: 'disconnected' | 'syncing' | 'connected';
  displayName?: string;
  phoneNumber?: string;
  instanceLabel?: string;
  instanceName?: string;
  lastSeen?: string;
  qrCode?: string;
  autoReply?: boolean;
  antiBanEnabled?: boolean;
  antiBanReadDelay?: number;
  antiBanTypingSpeed?: 'fast' | 'natural' | 'relaxed';
  whatsappFormattingStyle?: 'sales_promo' | 'business_corporate' | 'friendly_concierge' | 'technical_expert' | 'direct_short';
  isPersonalWhatsApp?: boolean;
  ownerPauseMinutes?: number;
  isCustomizable?: boolean;
  [key: string]: unknown;
}

type EvoStatus = 'DISCONNECTED' | 'LOADING_QR' | 'SCAN_READY' | 'CONNECTED';

const STATUS_CONFIG: Record<EvoStatus, { label: string; color: string; dotColor: string; icon: React.ComponentType<{ size?: number; className?: string }> }> = {
  DISCONNECTED: { label: 'DISCONNECTED', color: 'text-rose-300 bg-rose-500/20 border-rose-500/40', dotColor: 'bg-rose-400', icon: WifiOff },
  LOADING_QR: { label: 'CONNECTING...', color: 'text-amber-300 bg-amber-500/20 border-amber-500/40', dotColor: 'bg-amber-400 animate-pulse', icon: Loader2 },
  SCAN_READY: { label: 'SCAN QR CODE', color: 'text-sky-300 bg-sky-500/20 border-sky-500/40', dotColor: 'bg-sky-400 animate-pulse', icon: QrCode },
  CONNECTED: { label: 'CONNECTED & ACTIVE', color: 'text-emerald-300 bg-emerald-500/20 border-emerald-500/40', dotColor: 'bg-emerald-400', icon: Wifi },
};

export const WhatsAppInstanceNode = memo(({ id, data, selected }: NodeProps) => {
  const { setNodes, getEdges, getNodes } = useReactFlow();
  const nodeData = (data || {}) as WhatsAppInstanceNodeData;
  const { isTenantView, authToken, tenantApiKey, tenant, activeNodeIds, pulseState, triggerPulse, openPortalData, isCompactView, openInspector } = useTenantView();
  const isWired = useMemo(() => isNodeWiredToLunaCore(id, getEdges(), getNodes()), [id, getEdges, getNodes]);

  const apiMode = nodeData.apiMode || 'unofficial';
  const [showMetaToken, setShowMetaToken] = useState<boolean>(false);
  const [copiedWebhookUrl, setCopiedWebhookUrl] = useState<boolean>(false);
  const [copiedUniversalUrl, setCopiedUniversalUrl] = useState<boolean>(false);
  const [copiedVerifyToken, setCopiedVerifyToken] = useState<boolean>(false);
  const [copiedSipHost, setCopiedSipHost] = useState<boolean>(false);
  const [copiedSipPort, setCopiedSipPort] = useState<boolean>(false);
  const [copiedSipAppId, setCopiedSipAppId] = useState<boolean>(false);
  const [copiedSipParamName, setCopiedSipParamName] = useState<boolean>(false);
  const [copiedSipParamValue, setCopiedSipParamValue] = useState<boolean>(false);

  const effectiveTenantId = tenant?.apiKey || tenant?.tenantId || (tenant?.id ? String(tenant.id) : (tenantApiKey || 'YOUR_TENANT_KEY'));
  const baseUrl = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : 'https://kangavena.com';
  const universalWebhookUrl = `${baseUrl}/api/tenet/webhook/whatsapp`;
  const dedicatedWebhookUrl = `${baseUrl}/api/tenet/webhook/whatsapp/${effectiveTenantId}`;
  const webhookUrl = dedicatedWebhookUrl;
  
  // SIP Defaults & Official Meta Tech Provider App ID
  const KANGAVENA_OFFICIAL_META_APP_ID = '1556883522786680';
  const sipServerHost = nodeData.sipServerHost || 'wa.kangavena.com';
  const sipPort = nodeData.sipPort || 5061;
  const isOldDummyAppId = nodeData.sipAppId === '2463786290783462';
  const sipAppId = (nodeData.sipAppId && !isOldDummyAppId) ? nodeData.sipAppId : KANGAVENA_OFFICIAL_META_APP_ID;
  const isCustomIsolatedApp = Boolean(nodeData.sipAppId && nodeData.sipAppId !== KANGAVENA_OFFICIAL_META_APP_ID && !isOldDummyAppId);
  const sipParamName = nodeData.sipCustomParamName || 'tenant_id';
  const sipParamValue = nodeData.sipCustomParamValue || (tenant?.id ? String(tenant.id) : (effectiveTenantId || '11'));
  const enableSipCalling = nodeData.enableSipCalling ?? true;
  
  // Clean auto-generated verify token for this specific tenant
  const tenantDefaultToken = (effectiveTenantId && effectiveTenantId !== 'YOUR_TENANT_KEY') ? `kangavena_wa_${effectiveTenantId.slice(0, 16)}` : 'kangavena_wa_verify_token';

  // ── Chat Deletion & Memory Reset Engine ──
  const [showDeleteChatsModal, setShowDeleteChatsModal] = useState<boolean>(false);
  const [isClearingChats, setIsClearingChats] = useState<boolean>(false);
  const [clearChatStatus, setClearChatStatus] = useState<string | null>(null);

  const handleClearAllWhatsAppChats = async () => {
    if (isClearingChats) return;
    setIsClearingChats(true);
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
      if (tenantApiKey) headers['x-tenant-api-key'] = tenantApiKey;

      const res = await fetch('/api/tenet/live-chats/delete-all', {
        method: 'POST',
        headers,
        body: JSON.stringify({ channel: 'whatsapp' })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setClearChatStatus(`✅ Deleted ${data.deletedCount || 0} chats. Luna AI memory context reset!`);
        setTimeout(() => setClearChatStatus(null), 5000);
        setShowDeleteChatsModal(false);
      } else {
        alert(data.error || 'Failed to delete WhatsApp chats');
      }
    } catch (err: any) {
      console.error('[WhatsAppInstanceNode] Error deleting chats:', err);
      alert('Network error deleting WhatsApp chats');
    } finally {
      setIsClearingChats(false);
    }
  };

  const isInvalidOrForeignToken = useCallback((token?: string | null): boolean => {
    if (!token || !token.trim()) return true;
    const lower = token.trim().toLowerCase();
    if (lower === 'kangavena' || lower === 'kangavena_wa' || lower === 'kangavena_wa_verify_token' || lower === 'token' || lower === 'default' || lower === 'kangavena_wa_template') return true;
    if (token.startsWith('kangavena_wa_') && effectiveTenantId && effectiveTenantId !== 'YOUR_TENANT_KEY') {
      const clean8 = effectiveTenantId.replace(/^TENET_/i, '').slice(0, 8);
      const idPart = tenant?.id ? String(tenant.id) : '';
      if (!token.includes(effectiveTenantId.slice(0, 8)) && (clean8 && !token.includes(clean8)) && (!idPart || !token.includes(idPart))) {
        return true;
      }
    }
    return false;
  }, [effectiveTenantId, tenant]);

  const isStaleOrForeignToken = isInvalidOrForeignToken(nodeData.metaVerifyToken);
  const verifyToken = isStaleOrForeignToken ? tenantDefaultToken : (nodeData.metaVerifyToken || tenantDefaultToken);



  const copyToClipboard = (text: string, type: 'url' | 'token' | 'universal') => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(text);
      if (type === 'url') {
        setCopiedWebhookUrl(true);
        setTimeout(() => setCopiedWebhookUrl(false), 2500);
      } else if (type === 'universal') {
        setCopiedUniversalUrl(true);
        setTimeout(() => setCopiedUniversalUrl(false), 2500);
      } else {
        setCopiedVerifyToken(true);
        setTimeout(() => setCopiedVerifyToken(false), 2500);
      }
    }
  };

  const pulseLevel = getNodePulseLevel(id, pulseState, activeNodeIds);
  const isNodeActive = pulseLevel === 'active';
  const isNodeTrail = pulseLevel === 'trail';

  // Sri Lanka & International phone normalization
  const normalizeSriLankaPhone = (phone?: string): string => {
    if (!phone) return '';
    const trimmed = phone.trim();
    const digits = trimmed.replace(/\D/g, '');
    if (digits.startsWith('0') && digits.length === 10) {
      return '+94' + digits.slice(1);
    }
    if (digits.startsWith('94') && digits.length === 11) {
      return '+' + digits;
    }
    if (trimmed.startsWith('+')) return trimmed;
    if (digits.length >= 9) return '+' + digits;
    return trimmed;
  };

  // Sanitize initial phone: strip known dummy numbers (+1987654321, +94771234567, etc.)
  const cleanInitialPhone = (phone?: string): string => {
    if (!phone) return '';
    const clean = phone.trim();
    if (
      clean === '+1987654321' || clean === '1987654321' ||
      clean === '+1234567890' || clean === '1234567890' ||
      clean === '+94771234567' || clean === '94771234567' ||
      clean === '+94770000000' || clean === '94770000000'
    ) return '';
    return clean;
  };

  const rawTenantCandidatePhone = 
    (tenant && typeof tenant.get === 'function' ? tenant.get('whatsappPhone') : tenant?.whatsappPhone) ||
    tenant?.customFields?.metaPhoneNumber ||
    tenant?.registeredPhone ||
    tenant?.customFields?.registeredPhone ||
    tenant?.customFields?.managerWhatsapp ||
    tenant?.customFields?.ownerPhone ||
    tenant?.phone ||
    '';

  const autoDetectedRegisteredPhone = normalizeSriLankaPhone(cleanInitialPhone(rawTenantCandidatePhone));
  const rawNodePhone = cleanInitialPhone(nodeData.phoneNumber);
  const initialPhone = rawNodePhone ? normalizeSriLankaPhone(rawNodePhone) : autoDetectedRegisteredPhone;

  const isPrimaryNode = !id || id === 'whatsappNode-1';

  // Derive initial status without fake/dummy states
  const initialStatus: EvoStatus = (
    (nodeData.connectionStatus === 'connected' || (isPrimaryNode && tenant?.whatsappStatus === 'connected')) &&
    (initialPhone || autoDetectedRegisteredPhone)
  )
    ? 'CONNECTED'
    : (nodeData.connectionStatus === 'syncing' && nodeData.qrCode)
    ? 'SCAN_READY'
    : 'DISCONNECTED';

  const defaultTenantInstance = isTenantView && tenant?.id 
    ? (!isPrimaryNode ? `tenet_${tenant.id}_node_${id.replace(/[^a-zA-Z0-9_-]/g, '_')}` : `tenet_${tenant.id}`) 
    : (isTenantView && tenantApiKey ? `tenet_${tenantApiKey.slice(0, 8)}` : '');
  const initialInstance = nodeData.instanceName || defaultTenantInstance || '';

  const [evoStatus, setEvoStatus] = useState<EvoStatus>(initialStatus);
  const [instanceName, setInstanceName] = useState<string>(initialInstance);
  const [qrCode, setQrCode] = useState<string | null>(nodeData.qrCode || null);
  const [error, setError] = useState<string | null>(null);
  const [linkedPhone, setLinkedPhone] = useState<string>(initialPhone || autoDetectedRegisteredPhone);
  const [isCheckingStatus, setIsCheckingStatus] = useState<boolean>(false);
  const [isDisconnecting, setIsDisconnecting] = useState<boolean>(false);
  const [saveIndicator, setSaveIndicator] = useState<string | null>(null);
  const [showManualDeveloperSettings, setShowManualDeveloperSettings] = useState<boolean>(Boolean(nodeData.useCustomMetaToken));
  const [showSimulatorModal, setShowSimulatorModal] = useState<boolean>(false);
  const debounceTimerRef = useRef<any>(null);

  const statusCfg = STATUS_CONFIG[evoStatus];
  const pollTimerRef = useRef<any>(null);

  const persistNodeDataDirectly = useCallback((field: keyof WhatsAppInstanceNodeData, value: any, allUpdates?: Partial<WhatsAppInstanceNodeData>) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const currentNodes = typeof getNodes === 'function' ? getNodes() : [];
        const thisNode = currentNodes.find((n: any) => n.id === id);
        const activeData = (thisNode?.data || nodeData || {}) as WhatsAppInstanceNodeData;
        const payloadData = {
          ...activeData,
          [field]: value,
          ...(allUpdates || {})
        };

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        if (tenantApiKey) {
          headers['x-tenant-api-key'] = tenantApiKey;
        }
        if (effectiveTenantId && effectiveTenantId !== 'YOUR_TENANT_KEY') {
          headers['x-tenant-id'] = effectiveTenantId;
        }

        const endpoint = isTenantView ? '/api/tenet/widgets/save-node-data' : '/api/admin/luna-flows/save-node-data';
        const res = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            nodeId: id,
            widgetType: 'WHATSAPP_INSTANCE',
            data: payloadData,
            widgetData: payloadData,
            tenantId: effectiveTenantId !== 'YOUR_TENANT_KEY' ? effectiveTenantId : undefined,
          }),
        });

        if (res.ok) {
          setSaveIndicator('SAVED');
          setTimeout(() => setSaveIndicator(null), 2000);
        }
      } catch (err) {
        console.warn('[WhatsAppInstanceNode] Direct persist warning:', err);
      }
    }, 600);
  }, [id, nodeData, authToken, tenantApiKey, isTenantView, effectiveTenantId, getNodes]);

  const handleChange = useCallback((field: keyof WhatsAppInstanceNodeData, value: any) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: {
              ...node.data,
              [field]: value,
            },
          };
        }
        return node;
      })
    );
    persistNodeDataDirectly(field, value);
  }, [id, setNodes, persistNodeDataDirectly]);

  const [isLaunchingMetaSignup, setIsLaunchingMetaSignup] = useState<boolean>(false);
  const [isDiscoveringAccounts, setIsDiscoveringAccounts] = useState<boolean>(false);
  const [discoveredAccounts, setDiscoveredAccounts] = useState<any[]>([]);
  const [showDiscoveryModal, setShowDiscoveryModal] = useState<boolean>(false);

  // ⚡ Autonomous Manual Meta Cloud API Sync State
  const [isSyncingMetaApi, setIsSyncingMetaApi] = useState<boolean>(false);
  const [metaSyncStatus, setMetaSyncStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [registrationPin, setRegistrationPin] = useState<string>('123456');
  const [showPinInput, setShowPinInput] = useState<boolean>(false);

  // 🔍 Auto-Discover linked Meta accounts
  const handleDiscoverAccounts = useCallback(async (openModal = true) => {
    try {
      setIsDiscoveringAccounts(true);
      const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (tenantApiKey) headers['x-tenant-api-key'] = tenantApiKey;

      const res = await fetch('/api/tenet/whatsapp/meta-discovered-accounts', { headers });
      const data = await res.json().catch(() => null);
      if (data?.success && Array.isArray(data.accounts)) {
        setDiscoveredAccounts(data.accounts);
        if (openModal) {
          setShowDiscoveryModal(true);
        }
        return data.accounts;
      } else {
        if (openModal) alert('No authorized WhatsApp accounts found under Meta Partner: ' + (data?.error || 'Unknown response'));
        return [];
      }
    } catch (err: any) {
      if (openModal) alert('Failed to query Meta Partner accounts: ' + (err?.message || err));
      return [];
    } finally {
      setIsDiscoveringAccounts(false);
    }
  }, [authToken, tenantApiKey]);

  // ⚡ 1-Click Bind Discovered Phone Number
  const handleBindDiscoveredPhone = useCallback(async (wabaId: string, phone: any, wabaName: string) => {
    try {
      const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
      const bindHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) bindHeaders['Authorization'] = `Bearer ${token}`;
      if (tenantApiKey) bindHeaders['x-tenant-api-key'] = tenantApiKey;

      const bindRes = await fetch('/api/tenet/whatsapp/meta-sync-waba', {
        method: 'POST',
        headers: bindHeaders,
        body: JSON.stringify({
          phoneNumberId: phone.id,
          wabaId: wabaId,
          businessId: '',
          pin: registrationPin || '123456',
        }),
      });
      const bindData = await bindRes.json().catch(() => null);
      if (bindData?.success) {
        const boundPhone = bindData.phoneNumber || phone.displayPhone || phone.cleanPhone;
        handleChange('metaPhoneNumberId', phone.id);
        handleChange('metaWabaId', wabaId);
        handleChange('phoneNumber', boundPhone);
        setLinkedPhone(boundPhone);
        handleChange('connectionStatus', 'connected');
        handleChange('apiMode', 'official');
        if (bindData.verifiedName) {
          handleChange('displayName', bindData.verifiedName);
        }
        setShowDiscoveryModal(false);
        alert(`🎉 WhatsApp Connected Successfully!\nAccount: ${wabaName}\nPhone: ${boundPhone}\nWABA ID: ${wabaId}\nLuna AI is now active on this number!`);
      } else {
        alert('Binding notice: ' + (bindData?.error || 'Failed to bind WhatsApp'));
      }
    } catch (err: any) {
      alert('Failed to bind account: ' + (err?.message || err));
    }
  }, [authToken, tenantApiKey, registrationPin, handleChange]);

  // ⚡ Manual Autonomous Meta API Sync & Verify
  const handleManualMetaSync = useCallback(async () => {
    const pId = String(nodeData.metaPhoneNumberId || '').trim();
    const wId = String(nodeData.metaWabaId || '').trim();
    if (!pId || !wId) {
      alert('Please enter both Phone Number ID and WABA Account ID before syncing.');
      return;
    }

    try {
      setIsSyncingMetaApi(true);
      setMetaSyncStatus(null);
      const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (tenantApiKey) headers['x-tenant-api-key'] = tenantApiKey;

      const res = await fetch('/api/tenet/whatsapp/meta-sync-waba', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          phoneNumberId: pId,
          wabaId: wId,
          pin: registrationPin.trim() || '123456',
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        const boundPhone = data.phoneNumber || data.displayPhone || '';
        handleChange('metaPhoneNumberId', pId);
        handleChange('metaWabaId', wId);
        if (boundPhone) {
          handleChange('phoneNumber', boundPhone);
          setLinkedPhone(boundPhone);
        }
        if (data.verifiedName) {
          handleChange('displayName', data.verifiedName);
        }
        handleChange('connectionStatus', 'connected');
        handleChange('apiMode', 'official');
        setMetaSyncStatus({
          type: 'success',
          message: `✅ Synced & Active! ${boundPhone || pId} (${data.verifiedName || 'Meta Official API'}) is connected to Luna AI.`,
        });
        setTimeout(() => setMetaSyncStatus(null), 8000);
      } else {
        setMetaSyncStatus({
          type: 'error',
          message: data.error || 'Failed to sync Meta WABA. Please check IDs and 2FA PIN.',
        });
      }
    } catch (err: any) {
      setMetaSyncStatus({
        type: 'error',
        message: 'Network error syncing Meta WABA: ' + (err?.message || err),
      });
    } finally {
      setIsSyncingMetaApi(false);
    }
  }, [nodeData.metaPhoneNumberId, nodeData.metaWabaId, registrationPin, authToken, tenantApiKey, handleChange]);

  const handleLaunchMetaEmbeddedSignup = useCallback(async () => {
    try {
      setIsLaunchingMetaSignup(true);
      const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (tenantApiKey) headers['x-tenant-api-key'] = tenantApiKey;

      const res = await fetch('/api/tenet/whatsapp/meta-embedded-config', { headers });
      const cfg = await res.json().catch(() => ({}));
      const appId = cfg.appId || '1556883522786680';
      const configId = cfg.configId || '1953966058608610';
      const featureType = cfg.featureType || 'whatsapp_business_app_onboarding';

      const redirectUri = cfg.redirectUri || `${window.location.origin}/api/tenet/whatsapp/meta-callback`;
      const tenantState = tenant?.id ? String(tenant.id) : (effectiveTenantId || '');
      const url = `https://business.facebook.com/messaging/whatsapp/onboard/?app_id=${appId}&config_id=${configId}&redirect_uri=${encodeURIComponent(redirectUri)}&state=${encodeURIComponent(tenantState)}&extras=${encodeURIComponent(JSON.stringify({ sessionInfoVersion: '3', version: 'v4', featureType }))}`;
      
      const width = 640;
      const height = 750;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      const popup = window.open(url, 'whatsapp_embedded_signup', `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`);

      // 🔄 Real-Time Polling for Meta Onboarding Completion
      const pollTimer = setInterval(async () => {
        try {
          if (popup && popup.closed) {
            clearInterval(pollTimer);
            window.removeEventListener('message', messageHandler);
            setIsLaunchingMetaSignup(false);
            const latestAccounts = await handleDiscoverAccounts(false);
            if (latestAccounts && latestAccounts.length === 1 && latestAccounts[0].phones?.length === 1) {
              const singleAcc = latestAccounts[0];
              const singlePhone = singleAcc.phones[0];
              await handleBindDiscoveredPhone(singleAcc.wabaId, singlePhone, singleAcc.wabaName);
            } else if (latestAccounts && latestAccounts.length > 0) {
              setShowDiscoveryModal(true);
            }
          }
        } catch (e) {
          // ignore polling check glitch
        }
      }, 3000);

      const messageHandler = async (event: MessageEvent) => {
        if (!event.origin.includes('facebook.com') && event.origin !== window.location.origin) return;
        try {
          const payload = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          if (payload?.type === 'WA_EMBEDDED_SIGNUP' && payload?.event === 'FINISH') {
            clearInterval(pollTimer);
            window.removeEventListener('message', messageHandler);
            if (popup && !popup.closed) popup.close();

            const { phone_number_id, waba_id, business_id, phoneNumber } = payload.data || {};
            if (phone_number_id && waba_id) {
              const bindHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
              if (token) bindHeaders['Authorization'] = `Bearer ${token}`;
              if (tenantApiKey) bindHeaders['x-tenant-api-key'] = tenantApiKey;

              const bindRes = await fetch('/api/tenet/whatsapp/meta-embedded-signup', {
                method: 'POST',
                headers: bindHeaders,
                body: JSON.stringify({
                  phoneNumberId: phone_number_id,
                  wabaId: waba_id,
                  businessId: business_id,
                }),
              });
              const bindData = await bindRes.json().catch(() => null);
              if (bindData?.success) {
                const boundPhone = bindData.phoneNumber || phoneNumber || phone_number_id;
                handleChange('metaPhoneNumberId', phone_number_id);
                handleChange('metaWabaId', waba_id);
                handleChange('phoneNumber', boundPhone);
                setLinkedPhone(boundPhone);
                handleChange('connectionStatus', 'connected');
                handleChange('apiMode', 'official');
                alert(`🎉 WhatsApp Connected Successfully!\nPhone: ${boundPhone}\nWABA ID: ${waba_id}\nLuna AI is now active on your WhatsApp!`);
              } else {
                alert('Connection notice: ' + (bindData?.error || 'Failed to auto-bind WhatsApp'));
              }
            } else {
              const latestAccounts = await handleDiscoverAccounts(false);
              if (latestAccounts && latestAccounts.length === 1 && latestAccounts[0].phones?.length === 1) {
                const singleAcc = latestAccounts[0];
                const singlePhone = singleAcc.phones[0];
                await handleBindDiscoveredPhone(singleAcc.wabaId, singlePhone, singleAcc.wabaName);
              } else if (latestAccounts && latestAccounts.length > 0) {
                setShowDiscoveryModal(true);
              }
            }
          }
        } catch (e) {
          // ignore foreign postMessage
        }
      };

      window.addEventListener('message', messageHandler);
    } catch (err: any) {
      alert('Failed to launch Meta Onboarding: ' + (err?.message || err));
    } finally {
      setIsLaunchingMetaSignup(false);
    }
  }, [authToken, tenantApiKey, handleChange, handleDiscoverAccounts, handleBindDiscoveredPhone, tenant, effectiveTenantId]);


  // Auto-heal stale or generic template tokens immediately, and auto-set official Meta App ID & registered phone
  useEffect(() => {
    if (effectiveTenantId && effectiveTenantId !== 'YOUR_TENANT_KEY' && isInvalidOrForeignToken(nodeData.metaVerifyToken)) {
      handleChange('metaVerifyToken', tenantDefaultToken);
    }
    if (!nodeData.sipAppId || nodeData.sipAppId === '2463786290783462') {
      handleChange('sipAppId', KANGAVENA_OFFICIAL_META_APP_ID);
    }
    if ((!nodeData.phoneNumber || !cleanInitialPhone(nodeData.phoneNumber)) && autoDetectedRegisteredPhone) {
      setLinkedPhone(autoDetectedRegisteredPhone);
      handleChange('phoneNumber', autoDetectedRegisteredPhone);
    } else if (nodeData.phoneNumber && !linkedPhone) {
      setLinkedPhone(nodeData.phoneNumber);
    }
  }, [effectiveTenantId, nodeData.metaVerifyToken, tenantDefaultToken, isInvalidOrForeignToken, nodeData.sipAppId, nodeData.phoneNumber, autoDetectedRegisteredPhone, linkedPhone, handleChange]);

  // ZERO-TRUST ARCHITECTURE: Purge managed system token only if BYOT is disabled
  useEffect(() => {
    if (nodeData.metaAccessToken && !nodeData.useCustomMetaToken) {
      handleChange('metaAccessToken', '');
    }
  }, [nodeData.useCustomMetaToken, handleChange]);

  const getAuthHeaders = useCallback((): Record<string, string> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    if (tenantApiKey) {
      headers['x-tenant-api-key'] = tenantApiKey;
    }

    return headers;
  }, [isTenantView, authToken, tenantApiKey]);

  // ── 1. Check Connection Status (Evolution API) ──
  const checkStatus = useCallback(async (isSilent = false) => {
    if (!isSilent) setIsCheckingStatus(true);

    try {
      if (isTenantView) {
        const queryParams = new URLSearchParams();
        if (id) queryParams.set('nodeId', id);
        const targetInstance = instanceName || nodeData.instanceName || defaultTenantInstance;
        if (targetInstance) queryParams.set('instanceName', targetInstance);
        const url = `/api/tenet/whatsapp/status${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
        const res = await fetch(url, {
          headers: getAuthHeaders(),
        });
        const result = await res.json().catch(() => null);

        if (res.ok && result?.success && result?.status) {
          const st = result.status;
          if (st.connected) {
            setEvoStatus('CONNECTED');
            setError(null);
            setQrCode(null);
            const phone = cleanInitialPhone(st.phone || result.phone);
            if (phone) {
              setLinkedPhone(phone);
              handleChange('phoneNumber', phone);
            }
            handleChange('connectionStatus', 'connected');
            if (pollTimerRef.current) clearInterval(pollTimerRef.current);
          } else if (st.qrcode || st.qrCode) {
            setQrCode(st.qrcode || st.qrCode);
            setEvoStatus('SCAN_READY');
            handleChange('connectionStatus', 'syncing');
          } else {
            // Keep SCAN_READY state while scanning QR code so it does not flicker/disappear
            setEvoStatus((prev) => (prev === 'SCAN_READY' ? 'SCAN_READY' : 'DISCONNECTED'));
          }
        }
      } else {
        const targetInstance = instanceName || nodeData.instanceName;
        if (!targetInstance) return;

        const res = await fetch(`/api/admin/whatsapp/status/${encodeURIComponent(targetInstance)}`, {
          headers: getAuthHeaders(),
        });
        const result = await res.json().catch(() => null);

        if (res.ok && result?.success) {
          const state = result.state?.toLowerCase();
          if (state === 'open' || state === 'connected') {
            setEvoStatus('CONNECTED');
            setError(null);
            setQrCode(null);
            const phone = cleanInitialPhone(result.phone);
            if (phone) {
              setLinkedPhone(phone);
              handleChange('phoneNumber', phone);
            }
            handleChange('connectionStatus', 'connected');
            if (pollTimerRef.current) clearInterval(pollTimerRef.current);
          } else if (result.qrcode || result.qrCode) {
            setQrCode(result.qrcode || result.qrCode);
            setEvoStatus('SCAN_READY');
            handleChange('connectionStatus', 'syncing');
          } else {
            setEvoStatus((prev) => (prev === 'SCAN_READY' ? 'SCAN_READY' : 'DISCONNECTED'));
          }
        }
      }
    } catch (err: any) {
      if (!isSilent) setError(err?.message || 'Failed to check status');
    } finally {
      if (!isSilent) setIsCheckingStatus(false);
    }
  }, [isTenantView, getAuthHeaders, instanceName, nodeData.instanceName, handleChange]);

  // ── 2. Automatic Polling when in SCAN_READY ──
  useEffect(() => {
    if (evoStatus === 'SCAN_READY' && apiMode === 'unofficial') {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      pollTimerRef.current = setInterval(() => {
        checkStatus(true);
      }, 3000);
    } else {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    }

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [evoStatus, apiMode, checkStatus]);

  // ── 3. Connect WhatsApp (Generate Live QR Code) ──
  const handleConnectWhatsApp = useCallback(async () => {
    setEvoStatus('LOADING_QR');
    setError(null);
    setQrCode(null);

    try {
      const headers = getAuthHeaders();

      if (isTenantView) {
        const targetInstance = instanceName.trim() || nodeData.instanceName || defaultTenantInstance;
        const res = await fetch('/api/tenet/whatsapp/connect', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            nodeId: id,
            instanceName: targetInstance,
            isPersonalWhatsApp: Boolean(nodeData.isPersonalWhatsApp),
            phoneNumber: linkedPhone || nodeData.phoneNumber,
            apiMode: apiMode || 'unofficial',
          }),
        });
        const result = await res.json().catch(() => null);

        if (!res.ok || !result?.success) {
          throw new Error(result?.error || 'Failed to initialize WhatsApp connection');
        }

        if (result.connected) {
          setEvoStatus('CONNECTED');
          const phone = cleanInitialPhone(result.phone);
          if (phone) {
            setLinkedPhone(phone);
            handleChange('phoneNumber', phone);
          }
          handleChange('connectionStatus', 'connected');
        } else if (result.qrcode) {
          setQrCode(result.qrcode);
          setEvoStatus('SCAN_READY');
          handleChange('connectionStatus', 'syncing');
          handleChange('qrCode', result.qrcode);
        } else {
          checkStatus(false);
        }
      } else {
        const targetInstance = instanceName.trim() || nodeData.instanceName || 'sales_whatsapp';
        const res = await fetch('/api/admin/whatsapp/connect', {
          method: 'POST',
          headers,
          body: JSON.stringify({ instanceName: targetInstance }),
        });
        const result = await res.json().catch(() => null);

        if (!res.ok || !result?.success) {
          throw new Error(result?.error || 'Failed to initialize Evolution WhatsApp session');
        }

        if (result.connected) {
          setEvoStatus('CONNECTED');
          const phone = cleanInitialPhone(result.phone);
          if (phone) {
            setLinkedPhone(phone);
            handleChange('phoneNumber', phone);
          }
          handleChange('connectionStatus', 'connected');
        } else if (result.qrcode) {
          setQrCode(result.qrcode);
          setEvoStatus('SCAN_READY');
          handleChange('connectionStatus', 'syncing');
          handleChange('qrCode', result.qrcode);
        } else {
          checkStatus(false);
        }
      }
    } catch (err: any) {
      setError(err?.message || 'Error connecting WhatsApp. Please try again.');
      setEvoStatus('DISCONNECTED');
    }
  }, [isTenantView, getAuthHeaders, instanceName, nodeData.instanceName, checkStatus, handleChange]);

  // ── 4. Disconnect WhatsApp Instance ──
  const handleDisconnectWhatsApp = useCallback(async () => {
    setIsDisconnecting(true);
    setError(null);

    try {
      const headers = getAuthHeaders();

      if (isTenantView) {
        const targetInstance = instanceName.trim() || nodeData.instanceName || defaultTenantInstance;
        const res = await fetch('/api/tenet/whatsapp/disconnect', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            nodeId: id,
            instanceName: targetInstance,
          }),
        });
        const result = await res.json().catch(() => null);

        if (!res.ok || !result?.success) {
          throw new Error(result?.error || 'Failed to disconnect WhatsApp');
        }

        setEvoStatus('DISCONNECTED');
        setQrCode(null);
        setLinkedPhone('');
        handleChange('connectionStatus', 'disconnected');
        handleChange('phoneNumber', '');
        handleChange('metaPhoneNumberId', '');
        handleChange('metaWabaId', '');
        handleChange('displayName', '');
        handleChange('qrCode', null);
      } else {
        const targetInstance = instanceName.trim() || nodeData.instanceName;
        const res = await fetch('/api/tenet/whatsapp/disconnect', {
          method: 'POST',
          headers,
          body: JSON.stringify({ instanceName: targetInstance }),
        });
        const result = await res.json().catch(() => null);

        if (!res.ok || !result?.success) {
          throw new Error(result?.error || 'Failed to disconnect WhatsApp session');
        }

        setEvoStatus('DISCONNECTED');
        setQrCode(null);
        setLinkedPhone('');
        handleChange('connectionStatus', 'disconnected');
        handleChange('phoneNumber', '');
        handleChange('metaPhoneNumberId', '');
        handleChange('metaWabaId', '');
        handleChange('displayName', '');
        handleChange('qrCode', null);
      }
    } catch (err: any) {
      setError(err?.message || 'Error disconnecting WhatsApp');
    } finally {
      setIsDisconnecting(false);
    }
  }, [isTenantView, getAuthHeaders, instanceName, nodeData.instanceName, handleChange]);

  const isOfficialConnected = apiMode === 'official' && (
    nodeData.connectionStatus === 'connected' || evoStatus === 'CONNECTED'
  ) && Boolean(nodeData.phoneNumber || nodeData.metaPhoneNumberId || linkedPhone);

  return (
    <div
      className={`group rounded-2xl border transition-all duration-300 w-[400px] backdrop-blur-xl relative select-none ${
        apiMode === 'official'
          ? 'bg-gradient-to-b from-[#0B1528]/98 via-[#060D1A]/98 to-[#03060D]/98 shadow-[0_20px_50px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.12)]'
          : 'bg-gradient-to-b from-[#081813]/98 via-[#05100D]/98 to-[#030806]/98 shadow-[0_20px_50px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.12)]'
      } ${
        isNodeActive
          ? 'border-cyan-300 ring-4 ring-cyan-400/60 shadow-[0_0_80px_rgba(34,211,238,0.8),inset_0_1px_0_0_rgba(255,255,255,0.2)] scale-[1.02] pulse-glow-active'
          : isNodeTrail
          ? 'border-emerald-400/70 ring-2 ring-emerald-400/30 shadow-[0_0_40px_rgba(52,211,153,0.4),inset_0_1px_0_0_rgba(255,255,255,0.15)]'
          : selected
          ? (apiMode === 'official' ? 'border-sky-400 ring-2 ring-sky-400/50 shadow-[0_0_35px_rgba(56,189,248,0.5),inset_0_1px_0_0_rgba(255,255,255,0.18)]' : 'border-emerald-400 ring-2 ring-emerald-400/50 shadow-[0_0_35px_rgba(16,185,129,0.5),inset_0_1px_0_0_rgba(255,255,255,0.18)]')
          : (apiMode === 'official' ? 'border-sky-500/35 hover:border-sky-400/70 shadow-[0_20px_50px_rgba(0,0,0,0.85),0_0_20px_rgba(56,189,248,0.15),inset_0_1px_0_0_rgba(255,255,255,0.12)]' : 'border-emerald-500/35 hover:border-emerald-400/70 shadow-[0_20px_50px_rgba(0,0,0,0.85),0_0_20px_rgba(16,185,129,0.15),inset_0_1px_0_0_rgba(255,255,255,0.12)]')
      }`}
    >
      {/* 🚀 QUICK JUMP TO PORTAL DATA & WHATSAPP WEB (Hover Popover) */}
      {openPortalData && (
        <div className="absolute -top-11 left-1/2 -translate-x-1/2 z-50 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-300 transform translate-y-1 group-hover:translate-y-0 flex items-center gap-1.5 whitespace-nowrap">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowSimulatorModal(true);
            }}
            className="nodrag px-3.5 py-1.5 rounded-full bg-gradient-to-r from-cyan-400 via-emerald-400 to-teal-400 hover:from-cyan-300 hover:to-teal-300 text-slate-950 text-[10px] font-black uppercase tracking-wider shadow-[0_0_20px_rgba(34,211,238,0.8)] hover:scale-105 transition-all flex items-center gap-1.5 border border-cyan-300/80 cursor-pointer backdrop-blur-md active:scale-95"
            title="Open interactive Real-Time WhatsApp Test Sandbox"
          >
            <Sparkles size={11} className="text-slate-950" />
            <span>🧪 REAL-TIME TEST</span>
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openPortalData('whatsapp_web', id);
            }}
            className="nodrag px-3.5 py-1.5 rounded-full bg-gradient-to-r from-emerald-400 to-teal-500 hover:from-emerald-300 hover:to-teal-400 text-slate-950 text-[10px] font-black uppercase tracking-wider shadow-[0_0_20px_rgba(16,185,129,0.8)] hover:scale-105 transition-all flex items-center gap-1.5 border border-emerald-300/80 cursor-pointer backdrop-blur-md active:scale-95"
            title="Open authentic WhatsApp Web & Mobile Console for live chats"
          >
            <span>💬 WHATSAPP WEB</span>
            <ArrowRight size={11} className="text-slate-950" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openPortalData('whatsappNode', id);
            }}
            className={`nodrag px-3 py-1.5 rounded-full text-slate-950 text-[10px] font-black uppercase tracking-wider shadow-lg hover:scale-105 transition-all flex items-center gap-1.5 border cursor-pointer backdrop-blur-md active:scale-95 ${
              apiMode === 'official'
                ? 'bg-gradient-to-r from-sky-400 via-cyan-400 to-indigo-400 border-sky-300/60 shadow-[0_0_20px_rgba(56,189,248,0.6)]'
                : 'bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-500 border-emerald-300/60 shadow-[0_0_20px_rgba(16,185,129,0.6)]'
            }`}
            title="Open Portal Data tab for live WhatsApp instance data, device linking, and telemetry"
          >
            <span>📱 PORTAL DATA</span>
            <ArrowRight size={11} className="text-slate-950" />
          </button>
        </div>
      )}

      {/* Directional Handles for Graph Interconnections */}
      <Handle
        type="target"
        position={Position.Left}
        id="in-whatsapp"
        className={`!w-4 !h-4 !border-2 !border-slate-950 !-left-2 z-20 transition-all hover:scale-125 ${
          apiMode === 'official' ? '!bg-sky-400 shadow-[0_0_12px_rgba(56,189,248,1)]' : '!bg-emerald-400 shadow-[0_0_12px_rgba(16,185,129,1)]'
        }`}
        title="Input from Luna Core (out-whatsapp)"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="out-whatsapp-instance"
        className={`!w-4 !h-4 !border-2 !border-slate-950 !-right-2 z-20 transition-all hover:scale-125 ${
          apiMode === 'official' ? '!bg-sky-400 shadow-[0_0_12px_rgba(56,189,248,1)]' : '!bg-emerald-400 shadow-[0_0_12px_rgba(16,185,129,1)]'
        }`}
        title="Output to Luna Core (in-core)"
      />
      <Handle
        type="target"
        position={Position.Top}
        id="in-top"
        className={`!w-3.5 !h-3.5 !border-2 !border-slate-950 !-top-2 ${
          apiMode === 'official' ? '!bg-sky-400 shadow-[0_0_8px_rgba(56,189,248,0.8)]' : '!bg-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.8)]'
        }`}
      />

      {/* Header */}
      <div className={`px-4 py-3 border-b flex items-center justify-between rounded-t-2xl transition-colors ${
        apiMode === 'official'
          ? 'bg-gradient-to-r from-sky-950/80 via-indigo-950/60 to-slate-900/80 border-sky-500/30'
          : 'bg-gradient-to-r from-emerald-950/80 via-teal-950/60 to-slate-950/80 border-emerald-500/30'
      }`}>
        <div className="flex items-center gap-2.5">
          <div className={`p-1.5 rounded-lg border shadow-md ${
            apiMode === 'official'
              ? 'bg-sky-500/20 border-sky-500/40 text-sky-300 shadow-[0_0_12px_rgba(56,189,248,0.3)]'
              : 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300 shadow-[0_0_12px_rgba(16,185,129,0.3)]'
          }`}>
            {apiMode === 'official' ? <ShieldCheck size={16} /> : <Smartphone size={16} />}
          </div>
          <div>
            <h4 className="text-xs font-black text-slate-100 tracking-wide">WhatsApp Instance</h4>
            <p className={`text-[10px] font-mono ${apiMode === 'official' ? 'text-sky-300/80' : 'text-emerald-300/70'}`}>
              {apiMode === 'official' ? 'Meta Cloud API (Official Tier)' : 'Evolution API Gateway'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {saveIndicator && (
            <span className="text-[9px] font-mono font-bold text-emerald-400 animate-pulse bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/30">
              {saveIndicator}
            </span>
          )}
          {apiMode === 'official' ? (
            <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full border bg-sky-500/20 text-sky-300 border-sky-500/40 flex items-center gap-1.5 shadow-[0_0_10px_rgba(56,189,248,0.2)]">
              <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-pulse" />
              OFFICIAL WABA
            </span>
          ) : (
            <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full border flex items-center gap-1.5 ${statusCfg.color}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dotColor}`} />
              {statusCfg.label}
            </span>
          )}
        </div>
      </div>

      {/* Wiring Live Status Bar */}
      <div className="mx-4 mt-3">
        {isWired ? (
          <div className="px-2.5 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] flex items-center justify-between font-mono">
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <Zap size={11} className="text-emerald-400" /> Wired to Luna Core Hub
            </span>
            <span className="text-[9px] font-bold text-emerald-400 uppercase tracking-wide">ACTIVE</span>
          </div>
        ) : (
          <div className="px-2.5 py-1 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[10px] flex items-center justify-between font-mono">
            <span className="flex items-center gap-1.5">
              <AlertCircle size={11} className="text-amber-400" /> Disconnected
            </span>
            <span className="text-[9px] text-amber-400/80">Connect to Luna Core Hub</span>
          </div>
        )}
      </div>

      {/* 🧪 Real-Time WhatsApp Test Sandbox Button */}
      <div className="mx-4 mt-2.5">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setShowSimulatorModal(true);
          }}
          className="w-full nodrag py-2 px-3 rounded-xl bg-gradient-to-r from-emerald-500/20 via-cyan-500/20 to-emerald-500/20 hover:from-emerald-500/30 hover:via-cyan-500/30 hover:to-emerald-500/30 border border-emerald-400/40 hover:border-emerald-300 text-emerald-200 text-xs font-bold flex items-center justify-center gap-2 shadow-lg transition-all group/btn cursor-pointer active:scale-95"
          title="Open interactive WhatsApp chat simulator (Owner Mode & Customer Mode)"
        >
          <Sparkles className="w-3.5 h-3.5 text-cyan-400 animate-pulse group-hover/btn:rotate-12 transition-transform" />
          <span>REAL-TIME WHATSAPP TEST</span>
          <ArrowRight className="w-3.5 h-3.5 text-emerald-400 group-hover/btn:translate-x-0.5 transition-transform" />
        </button>
      </div>

      {isCompactView ? (
        <div className="p-4 space-y-2.5 text-xs">
          <div className={`p-3 rounded-2xl bg-slate-900/80 border space-y-2.5 ${
            apiMode === 'official' ? 'border-sky-500/30' : 'border-emerald-500/30'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={`w-2.5 h-2.5 rounded-full animate-pulse shadow-md ${
                  apiMode === 'official' ? 'bg-sky-400 shadow-sky-400/80' : 'bg-emerald-400 shadow-emerald-400/80'
                }`} />
                <span className="font-bold text-white font-mono text-xs">
                  {nodeData.phoneNumber || linkedPhone || '+94 77 XXX XXXX'}
                </span>
              </div>
              <button
                type="button"
                onClick={() => openInspector && openInspector(id)}
                className={`nodrag px-2.5 py-1 rounded-lg text-[10px] font-bold border transition cursor-pointer active:scale-95 shadow-sm ${
                  apiMode === 'official'
                    ? 'bg-sky-500/20 hover:bg-sky-500/35 text-sky-200 border-sky-400/40'
                    : 'bg-emerald-500/20 hover:bg-emerald-500/35 text-emerald-200 border-emerald-400/40'
                }`}
              >
                Inspect ⚙️
              </button>
            </div>

            <div className="flex flex-wrap gap-1.5 pt-1">
              <span className={`px-2 py-0.5 rounded-md border text-[10px] font-mono font-bold ${
                apiMode === 'official'
                  ? 'bg-sky-950/70 border-sky-500/40 text-sky-300'
                  : 'bg-emerald-950/70 border-emerald-500/40 text-emerald-300'
              }`}>
                {apiMode === 'official' ? '🛡️ Meta Cloud API' : '⚡ Evolution QR'}
              </span>
              {nodeData.enableSipCalling !== false && (
                <span className="px-2 py-0.5 rounded-md bg-indigo-950/70 border border-indigo-500/40 text-[10px] font-mono text-indigo-300 font-bold">
                  📞 2-Way SIP Active
                </span>
              )}
              <span className="px-2 py-0.5 rounded-md bg-slate-800 border border-slate-700 text-[10px] font-mono text-slate-300">
                ⌨️ {nodeData.antiBanTypingSpeed || 'natural'} typing
              </span>
            </div>

            {openPortalData && (
              <div className="space-y-1.5 mt-1">
                <button
                  type="button"
                  onClick={() => openPortalData('whatsapp_web', id)}
                  className="nodrag w-full py-2 px-3 rounded-xl bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600 hover:from-emerald-400 hover:to-teal-400 text-slate-950 font-black text-xs flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20 cursor-pointer active:scale-95 transition-all"
                  title="Launch authentic WhatsApp Web & Mobile Console for live customer chats & human takeover"
                >
                  <span>💬 Open WhatsApp Web</span>
                  <ArrowRight size={13} className="text-slate-950" />
                </button>
                <div className="flex items-center justify-between px-2.5 py-1 rounded-lg bg-emerald-950/40 border border-emerald-500/30 text-[10px]">
                  <span className="font-bold text-emerald-300 flex items-center gap-1">
                    <span>🛍️</span> Orders & Leads Filtered
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShowDeleteChatsModal(true)}
                      className="nodrag text-[9px] font-bold text-rose-400 hover:text-rose-300 underline cursor-pointer flex items-center gap-0.5"
                      title="Delete WhatsApp chat history & reset Luna context"
                    >
                      <Trash2 size={9} />
                      <span>Delete</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => openPortalData('whatsapp_web', id)}
                      className="nodrag text-[9px] font-black uppercase text-emerald-300 hover:text-white underline cursor-pointer"
                    >
                      View →
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="p-4 space-y-3.5 text-xs">
          {/* ── DUAL-TIER ARCHITECTURE SELECTOR ── */}
        <div className="p-1 rounded-xl bg-slate-900/90 border border-white/10 grid grid-cols-2 gap-1 shadow-inner">
          <button
            type="button"
            onClick={() => handleChange('apiMode', 'unofficial')}
            className={`nodrag px-2.5 py-1.5 rounded-lg text-[10px] font-black tracking-wide flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
              apiMode === 'unofficial'
                ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-md shadow-emerald-500/20'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
            }`}
          >
            <Zap size={11} className={apiMode === 'unofficial' ? 'text-emerald-200' : 'text-slate-400'} />
            <span>Unofficial (Evolution)</span>
          </button>
          <button
            type="button"
            onClick={() => handleChange('apiMode', 'official')}
            className={`nodrag px-2.5 py-1.5 rounded-lg text-[10px] font-black tracking-wide flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
              apiMode === 'official'
                ? 'bg-gradient-to-r from-sky-600 to-indigo-600 text-white shadow-md shadow-sky-500/20'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
            }`}
          >
            <ShieldCheck size={11} className={apiMode === 'official' ? 'text-sky-200' : 'text-slate-400'} />
            <span>Official (Meta API)</span>
          </button>
        </div>

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* OPTION 1: OFFICIAL META CLOUD API CONFIGURATION PANEL            */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        {apiMode === 'official' ? (
          <div className="space-y-3">
            {/* 🟢 Live Connected Meta Cloud API Status HUD Card */}
            {isOfficialConnected ? (
              <div className="p-3.5 rounded-2xl bg-gradient-to-br from-emerald-950/70 via-slate-900 to-cyan-950/50 border border-emerald-500/50 shadow-xl space-y-2.5 relative overflow-hidden">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                    </span>
                    <span className="text-xs font-black text-emerald-300 tracking-wide flex items-center gap-1.5 font-sans">
                      <ShieldCheck size={14} className="text-emerald-400" />
                      Meta Cloud API Connected
                    </span>
                  </div>
                  <span className="text-[9px] font-mono font-black uppercase px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                    LIVE ACTIVE
                  </span>
                </div>

                <div className="p-2.5 rounded-xl bg-slate-950/80 border border-white/5 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <div className="text-[11px] font-black text-white flex items-center gap-1.5">
                      <Building2 size={13} className="text-cyan-400" />
                      <span>{nodeData.displayName || tenant?.name || 'Official WhatsApp Business'}</span>
                    </div>
                    <span className="text-[9px] font-mono text-emerald-400 font-bold bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/20">
                      Verified
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone size={13} className="text-emerald-400" />
                    <span className="text-sm font-black text-emerald-300 font-mono tracking-wider">
                      {nodeData.phoneNumber || linkedPhone || autoDetectedRegisteredPhone || 'Connected'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 pt-1 text-[8.5px] font-mono text-slate-400 border-t border-white/5">
                    <div>WABA: <span className="text-slate-200">{nodeData.metaWabaId || 'Auto-Synced'}</span></div>
                    <div>Phone ID: <span className="text-slate-200">{nodeData.metaPhoneNumberId || 'Auto-Synced'}</span></div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 pt-0.5">
                  <button
                    type="button"
                    onClick={() => handleDiscoverAccounts(true)}
                    disabled={isDiscoveringAccounts}
                    className="w-full py-2 px-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 border border-white/10 text-slate-200 font-bold text-xs flex items-center justify-center gap-1.5 cursor-pointer transition-colors active:scale-98"
                    title="Re-scan Meta Partner or switch connected business numbers"
                  >
                    <RefreshCw size={12} className={isDiscoveringAccounts ? "animate-spin text-cyan-400" : "text-cyan-400"} />
                    <span>{isDiscoveringAccounts ? 'Scanning...' : 'Re-sync / Switch'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleDisconnectWhatsApp}
                    disabled={isDisconnecting}
                    className="w-full py-2 px-2.5 rounded-xl bg-rose-950/60 hover:bg-rose-900/80 border border-rose-500/40 text-rose-300 hover:text-rose-100 font-bold text-xs flex items-center justify-center gap-1.5 cursor-pointer transition-colors shadow-sm active:scale-98"
                  >
                    <PowerOff size={12} className={isDisconnecting ? "animate-spin text-rose-400" : "text-rose-400"} />
                    <span>{isDisconnecting ? 'Disconnecting...' : 'Log Out / Disconnect'}</span>
                  </button>
                </div>
              </div>
            ) : (
              /* 🚀 1-Click Meta Embedded Signup Quick Action Banner */
              <div className="p-3 rounded-2xl bg-gradient-to-r from-cyan-950/60 via-blue-950/50 to-indigo-950/60 border border-cyan-500/40 space-y-2 shadow-lg relative overflow-hidden">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Sparkles size={13} className="text-cyan-400 animate-pulse" />
                    <span className="text-[11px] font-black text-white tracking-wide">
                      Meta Cloud API 1-Click Fast Connect
                    </span>
                  </div>
                  <span className="text-[8px] font-mono font-black uppercase px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-400/30">
                    OFFICIAL PARTNER
                  </span>
                </div>
                <p className="text-[10px] text-slate-300 leading-relaxed">
                  Connect your business WhatsApp number in 30 seconds with Meta popup dialog. Automatic WABA binding, 2FA registration, and webhook sync.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                  <button
                    type="button"
                    onClick={handleLaunchMetaEmbeddedSignup}
                    disabled={isLaunchingMetaSignup}
                    className="w-full py-2.5 px-3 rounded-xl bg-gradient-to-r from-cyan-500 hover:from-cyan-400 to-blue-600 hover:to-blue-500 text-white font-black text-xs shadow-md shadow-cyan-500/25 flex items-center justify-center gap-1.5 cursor-pointer transition-all active:scale-98 disabled:opacity-50"
                  >
                    {isLaunchingMetaSignup ? (
                      <RefreshCw size={13} className="animate-spin text-white" />
                    ) : (
                      <Building2 size={13} className="text-cyan-100" />
                    )}
                    <span>{isLaunchingMetaSignup ? 'Opening Meta...' : '🚀 1-Click Fast Connect'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleDiscoverAccounts(true)}
                    disabled={isDiscoveringAccounts}
                    className="w-full py-2.5 px-3 rounded-xl bg-slate-900/90 hover:bg-slate-800 border border-emerald-500/50 hover:border-emerald-400 text-emerald-300 hover:text-emerald-200 font-black text-xs shadow-md shadow-emerald-500/10 flex items-center justify-center gap-1.5 cursor-pointer transition-all active:scale-98 disabled:opacity-50"
                    title="Discover verified numbers already shared with Kangavena Meta Partner"
                  >
                    <RefreshCw size={13} className={isDiscoveringAccounts ? "animate-spin text-emerald-400" : "text-emerald-400"} />
                    <span>{isDiscoveringAccounts ? 'Scanning Meta...' : '⚡ Auto-Detect & Sync'}</span>
                  </button>
                </div>
              </div>
            )}

            {/* ⚡ Meta Discovered Accounts Modal */}
            {showDiscoveryModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-150">
                <div className="w-full max-w-lg bg-slate-900 border border-cyan-500/40 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
                  <div className="p-4 bg-gradient-to-r from-slate-950 via-slate-900 to-cyan-950/40 border-b border-white/10 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded-lg bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                        <Sparkles size={16} />
                      </div>
                      <div>
                        <h3 className="text-sm font-black text-white">Discovered Meta WhatsApp Accounts</h3>
                        <p className="text-[10px] text-slate-400">Select an authorized business number to connect to Luna AI</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowDiscoveryModal(false)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 cursor-pointer transition-colors"
                    >
                      <XCircle size={18} />
                    </button>
                  </div>

                  <div className="p-4 space-y-3 overflow-y-auto flex-1">
                    {discoveredAccounts.filter(a => a.phones?.length > 0).length === 0 ? (
                      <div className="p-6 text-center text-slate-400 space-y-2">
                        <AlertCircle size={28} className="mx-auto text-amber-400/80" />
                        <p className="text-xs font-bold text-slate-300">No authorized numbers found yet</p>
                        <p className="text-[11px] text-slate-500">
                          Please use "1-Click Fast Connect" to link your WhatsApp Business account with Kangavena.
                        </p>
                      </div>
                    ) : (
                      discoveredAccounts.filter(a => a.phones?.length > 0).map((account) => (
                        <div key={account.wabaId} className="p-3.5 rounded-xl bg-slate-950/70 border border-white/10 hover:border-cyan-500/40 transition-all space-y-2.5">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="text-xs font-black text-white flex items-center gap-1.5">
                                <Building2 size={13} className="text-cyan-400" />
                                {account.wabaName}
                              </h4>
                              <span className="text-[9px] font-mono text-slate-500">WABA ID: {account.wabaId}</span>
                            </div>
                            <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                              Authorized
                            </span>
                          </div>

                          <div className="space-y-1.5 pt-1">
                            {account.phones.map((phone: any) => (
                              <div key={phone.id} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-900/80 border border-white/5 hover:border-emerald-500/30">
                                <div className="space-y-0.5">
                                  <div className="flex items-center gap-2">
                                    <Phone size={12} className="text-emerald-400" />
                                    <span className="text-xs font-black text-emerald-300 font-mono">{phone.displayPhone}</span>
                                  </div>
                                  <div className="text-[9px] text-slate-400 flex items-center gap-2">
                                    <span>Name: {phone.verifiedName || 'Business Phone'}</span>
                                    <span>•</span>
                                    <span className="font-mono">ID: {phone.id}</span>
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  onClick={() => handleBindDiscoveredPhone(account.wabaId, phone, account.wabaName)}
                                  className="py-1.5 px-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs shadow-md shadow-emerald-500/20 flex items-center gap-1.5 cursor-pointer transition-all active:scale-95"
                                >
                                  <CheckCircle2 size={13} />
                                  <span>Connect</span>
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="p-3 bg-slate-950 border-t border-white/10 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => handleDiscoverAccounts(true)}
                      disabled={isDiscoveringAccounts}
                      className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1.5 cursor-pointer"
                    >
                      <RefreshCw size={12} className={isDiscoveringAccounts ? "animate-spin" : ""} />
                      <span>Re-scan Meta Partner</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowDiscoveryModal(false)}
                      className="py-1.5 px-4 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold cursor-pointer"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ⚙️ Custom Developer Mode & Manual Credentials (Accordion) */}
            <div className="rounded-xl border border-white/10 bg-slate-950/40 overflow-hidden">
              <button
                type="button"
                onClick={() => setShowManualDeveloperSettings(!showManualDeveloperSettings)}
                className="w-full p-2.5 flex items-center justify-between hover:bg-white/[0.02] cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Sliders size={13} className={showManualDeveloperSettings ? "text-sky-400" : "text-slate-400"} />
                  <span className="text-[10.5px] font-black uppercase tracking-wider text-slate-300">
                    Manual &amp; Custom Developer Settings
                  </span>
                  {nodeData.useCustomMetaToken && (
                    <span className="text-[8px] font-mono px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30 font-bold">
                      BYOT ACTIVE
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5 text-slate-400">
                  <span className="text-[9px] font-mono">
                    {showManualDeveloperSettings ? 'Hide' : 'Configure Custom API / IDs'}
                  </span>
                  {showManualDeveloperSettings ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                </div>
              </button>

              {showManualDeveloperSettings && (
                <div className="p-3 border-t border-white/5 space-y-3 animate-in fade-in slide-in-from-top-1 duration-200">
                  {/* Meta Access Token */}
                  <div className="p-2.5 rounded-xl border border-white/5 bg-slate-950/40">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                        <Key size={12} className="text-sky-400" /> Custom Meta Token (BYOT)
                      </label>
                      <div className="flex items-center gap-2">
                        <span className="text-[8px] font-bold text-slate-500 uppercase tracking-wider">
                          {nodeData.useCustomMetaToken ? 'Enabled' : 'Disabled'}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            const nextVal = !nodeData.useCustomMetaToken;
                            handleChange('useCustomMetaToken', nextVal);
                            if (!nextVal) {
                              handleChange('metaAccessToken', '');
                            }
                          }}
                          className={`relative inline-flex h-4 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none transition-colors ${nodeData.useCustomMetaToken ? 'bg-sky-500' : 'bg-slate-700'}`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-2.5 w-2.5 transform rounded-full bg-white shadow transition duration-200 ease-in-out ${nodeData.useCustomMetaToken ? 'translate-x-1.5' : '-translate-x-1.5'}`}
                          />
                        </button>
                      </div>
                    </div>

                    {!nodeData.useCustomMetaToken ? (
                      <div className="p-2 rounded-lg bg-emerald-950/40 border border-emerald-900/50 mt-1">
                        <p className="text-[9px] text-emerald-400/90 font-mono flex items-center gap-1.5">
                          <ShieldCheck size={12} className="text-emerald-400 shrink-0" />
                          <span>Kangavena Managed System Token is Active. Zero-Trust Mode engaged.</span>
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-1.5 mt-2 animate-in fade-in duration-200">
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() => setShowMetaToken(!showMetaToken)}
                            className="nodrag text-[9px] font-mono text-sky-400 hover:text-sky-300 flex items-center gap-1 cursor-pointer"
                          >
                            {showMetaToken ? <EyeOff size={10} /> : <Eye size={10} />}
                            <span>{showMetaToken ? 'HIDE' : 'SHOW'}</span>
                          </button>
                        </div>
                        <input
                          type={showMetaToken ? 'text' : 'password'}
                          autoComplete="new-password"
                          data-lpignore="true"
                          name="node_custom_meta_token_field"
                          className="nodrag nowheel select-text w-full bg-slate-900/90 border border-sky-500/30 rounded-xl px-3 py-2 text-slate-100 text-xs focus:outline-none focus:border-sky-400 focus:ring-1 focus:ring-sky-400/30 transition-all placeholder:text-slate-500 font-mono"
                          placeholder="Paste your custom EAA... token here"
                          value={nodeData.metaAccessToken || ''}
                          onChange={(e) => handleChange('metaAccessToken', e.target.value)}
                          onPaste={(e) => {
                            e.stopPropagation();
                            const pasted = e.clipboardData.getData('text');
                            if (pasted) {
                              handleChange('metaAccessToken', pasted.trim());
                            }
                          }}
                        />
                        <p className="text-[8px] text-amber-400/80 mt-1 font-sans flex items-center gap-1">
                          <span>⚠️ Warning: You are using an isolated custom token. Make sure it has correct permissions.</span>
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Phone Number ID & WABA ID Grid */}
                  <div className="grid grid-cols-2 gap-2.5">
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1 flex items-center gap-1">
                        <Smartphone size={11} className="text-sky-400" /> Phone Number ID
                      </label>
                      <input
                        type="text"
                        className="nodrag nowheel w-full bg-slate-900/90 border border-white/10 rounded-xl px-2.5 py-1.5 text-slate-100 text-xs focus:outline-none focus:border-sky-400 font-mono placeholder:text-slate-600"
                        placeholder="e.g. 104857291837465"
                        value={nodeData.metaPhoneNumberId || ''}
                        onChange={(e) => handleChange('metaPhoneNumberId', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1 flex items-center gap-1">
                        <Building2 size={11} className="text-indigo-400" /> WABA Account ID
                      </label>
                      <input
                        type="text"
                        className="nodrag nowheel w-full bg-slate-900/90 border border-white/10 rounded-xl px-2.5 py-1.5 text-slate-100 text-xs focus:outline-none focus:border-sky-400 font-mono placeholder:text-slate-600"
                        placeholder="e.g. 293847561029384"
                        value={nodeData.metaWabaId || ''}
                        onChange={(e) => handleChange('metaWabaId', e.target.value)}
                      />
                    </div>
                  </div>

                  {/* ⚡ Autonomous Meta API Auto-Sync & Connect Trigger */}
                  <div className="pt-0.5 space-y-2">
                    <div className="flex items-center justify-between text-[9px]">
                      <button
                        type="button"
                        onClick={() => setShowPinInput(!showPinInput)}
                        className="nodrag text-slate-400 hover:text-sky-300 font-mono flex items-center gap-1 transition-colors cursor-pointer"
                      >
                        <Lock size={9} className="text-slate-500" />
                        <span>2FA PIN: <span className="text-slate-300 font-bold">{showPinInput ? 'Custom' : '123456 (Default)'}</span></span>
                      </button>
                      {nodeData.metaPhoneNumberId && nodeData.metaWabaId && (
                        <span className="text-[8px] font-mono text-emerald-400/80 flex items-center gap-1">
                          <CheckCircle2 size={8} /> Credentials Ready
                        </span>
                      )}
                    </div>

                    {showPinInput && (
                      <div className="p-2 rounded-xl bg-slate-900/90 border border-white/10 space-y-1">
                        <label className="block text-[9px] font-mono text-slate-300">
                          Custom Meta 2-Step Verification PIN (6-digits)
                        </label>
                        <input
                          type="text"
                          maxLength={6}
                          className="nodrag nowheel w-full bg-slate-950 border border-white/10 rounded-lg px-2 py-1 text-slate-100 text-xs font-mono placeholder:text-slate-600 focus:outline-none focus:border-sky-400"
                          placeholder="123456"
                          value={registrationPin}
                          onChange={(e) => setRegistrationPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        />
                        <p className="text-[7.5px] text-slate-400">
                          Enter the 6-digit PIN set in Meta WhatsApp Manager (leave as 123456 if default).
                        </p>
                      </div>
                    )}

                    <button
                      type="button"
                      disabled={isSyncingMetaApi || !nodeData.metaPhoneNumberId || !nodeData.metaWabaId}
                      onClick={handleManualMetaSync}
                      className="nodrag w-full py-2 px-3 rounded-xl bg-gradient-to-r from-sky-600 via-indigo-600 to-purple-600 hover:from-sky-500 hover:via-indigo-500 hover:to-purple-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-mono text-[11px] font-bold tracking-wide flex items-center justify-center gap-2 shadow-lg shadow-sky-950/50 hover:shadow-sky-500/20 active:scale-[0.99] transition-all cursor-pointer"
                    >
                      {isSyncingMetaApi ? (
                        <>
                          <Loader2 size={13} className="animate-spin text-white" />
                          <span>Subscribing WABA & Verifying...</span>
                        </>
                      ) : (
                        <>
                          <Zap size={13} className="text-amber-300" />
                          <span>Auto-Sync & Connect Meta API</span>
                        </>
                      )}
                    </button>

                    {metaSyncStatus && (
                      <div className={`p-2 rounded-xl border text-[9.5px] font-mono flex items-start gap-1.5 ${
                        metaSyncStatus.type === 'success'
                          ? 'bg-emerald-950/60 border-emerald-500/40 text-emerald-200'
                          : 'bg-rose-950/60 border-rose-500/40 text-rose-200'
                      }`}>
                        {metaSyncStatus.type === 'success' ? (
                          <CheckCircle2 size={12} className="text-emerald-400 shrink-0 mt-0.5" />
                        ) : (
                          <AlertCircle size={12} className="text-rose-400 shrink-0 mt-0.5" />
                        )}
                        <span className="leading-snug">{metaSyncStatus.message}</span>
                      </div>
                    )}
                  </div>

                  {/* ── Auto-Generated Webhook Config Card (Copyable) ── */}
                  <div className="p-3 rounded-2xl bg-gradient-to-br from-sky-950/40 via-slate-900/90 to-indigo-950/40 border border-sky-500/30 space-y-2.5 shadow-inner">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase tracking-wider text-sky-300 flex items-center gap-1.5 font-mono">
                        <Link2 size={12} className="text-sky-400" /> Meta Webhook Callback Setup
                      </span>
                      <span className="text-[8px] font-mono font-bold px-1.5 py-0.5 rounded bg-sky-500/20 text-sky-300 border border-sky-500/30">
                        AUTO-GENERATED
                      </span>
                    </div>

                    {/* 1. Universal Main Callback URL */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1.5">
                          <label className="text-[9px] font-mono uppercase text-emerald-400 font-bold">
                            Main Callback URL
                          </label>
                          <span className="text-[7.5px] font-mono px-1 py-0.2 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-bold">
                            UNIVERSAL / ALL TENANTS
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(universalWebhookUrl, 'universal')}
                          className="nodrag text-[9px] font-mono text-emerald-400 hover:text-emerald-300 font-bold flex items-center gap-1 cursor-pointer transition-colors"
                        >
                          {copiedUniversalUrl ? <Check size={10} className="text-emerald-400" /> : <Copy size={10} />}
                          <span>{copiedUniversalUrl ? 'COPIED' : 'COPY'}</span>
                        </button>
                      </div>
                      <div className="text-[10px] font-mono text-emerald-200 bg-slate-950/80 px-2.5 py-1.5 rounded-xl border border-emerald-500/30 truncate select-all">
                        {universalWebhookUrl}
                      </div>
                      <p className="text-[8px] text-slate-400 mt-0.5 font-sans">
                        ⭐ Set <strong>once</strong> in your Meta App Dashboard. Dynamically routes all tenants by Phone Number ID. Never needs changing!
                      </p>
                    </div>

                    {/* 2. Dedicated Tenant URL (Optional) */}
                    <div className="pt-1.5 border-t border-slate-800/80">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1.5">
                          <label className="text-[9px] font-mono uppercase text-slate-400 font-bold">
                            Dedicated Tenant URL
                          </label>
                          <span className="text-[7.5px] font-mono px-1 py-0.2 rounded bg-slate-800 text-slate-400">
                            OPTIONAL / ISOLATED
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(dedicatedWebhookUrl, 'url')}
                          className="nodrag text-[9px] font-mono text-sky-400 hover:text-sky-300 font-bold flex items-center gap-1 cursor-pointer transition-colors"
                        >
                          {copiedWebhookUrl ? <Check size={10} className="text-emerald-400" /> : <Copy size={10} />}
                          <span>{copiedWebhookUrl ? 'COPIED' : 'COPY'}</span>
                        </button>
                      </div>
                      <div className="text-[9.5px] font-mono text-slate-400 bg-slate-950/60 px-2.5 py-1 rounded-xl border border-slate-800 truncate select-all">
                        {dedicatedWebhookUrl}
                      </div>
                      <p className="text-[7.5px] text-slate-500 mt-0.5 font-sans">
                        Only needed if this business tenant creates their own isolated private Meta Developer App.
                      </p>
                    </div>

                    {/* 3. Verify Token */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-[9px] font-mono uppercase text-slate-400 font-bold">
                          Verify Token
                        </label>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(verifyToken, 'token')}
                          className="nodrag text-[9px] font-mono text-sky-400 hover:text-sky-300 font-bold flex items-center gap-1 cursor-pointer transition-colors"
                        >
                          {copiedVerifyToken ? <Check size={10} className="text-emerald-400" /> : <Copy size={10} />}
                          <span>{copiedVerifyToken ? 'COPIED' : 'COPY'}</span>
                        </button>
                      </div>
                      <input
                        type="text"
                        className="nodrag nowheel w-full bg-slate-950/80 border border-sky-500/20 rounded-xl px-2.5 py-1.5 text-sky-200 text-[10px] font-mono focus:outline-none focus:border-sky-400 disabled:opacity-50"
                        value={isStaleOrForeignToken ? verifyToken : (nodeData.metaVerifyToken || verifyToken)}
                        onChange={(e) => handleChange('metaVerifyToken', e.target.value)}
                      />
                      <p className="text-[8px] text-slate-500 mt-0.5 font-sans">
                        Paste into Meta App ➜ WhatsApp ➜ Configuration ➜ Verify Token
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ── Meta SIP Real-Time Voice Calling Card (Phase 41) ── */}
            <div className="p-3 rounded-2xl bg-gradient-to-br from-violet-950/40 via-slate-900/90 to-indigo-950/40 border border-violet-500/30 space-y-3 shadow-inner">
              {/* Header with Enable Switch */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-violet-500/20 text-violet-300 border border-violet-500/40">
                    <PhoneCall size={13} />
                  </div>
                  <div>
                    <div className="text-[11px] font-black text-violet-200 tracking-wide flex items-center gap-1.5">
                      Direct Voice Calling (SIP)
                      <span className="text-[8px] font-mono font-bold px-1.5 py-0.5 rounded bg-violet-500/30 text-violet-200 border border-violet-400/40">
                        2-WAY AUDIO
                      </span>
                    </div>
                    <p className="text-[8.5px] text-slate-400">Answer WhatsApp calls in real-time with AI</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer nodrag">
                  <input
                    type="checkbox"
                    checked={enableSipCalling}
                    onChange={(e) => handleChange('enableSipCalling', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-8 h-4.5 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-3.5 after:w-3.5 after:transition-all peer-checked:bg-violet-600"></div>
                </label>
              </div>

              {enableSipCalling && (
                <div className="space-y-2.5 pt-1 border-t border-violet-500/20">
                  {/* Call Handling Mode */}
                  <div>
                    <label className="block text-[9.5px] font-bold uppercase tracking-wider text-violet-300 mb-1.5 flex items-center gap-1">
                      <Bot size={11} className="text-violet-400" /> AI Call Answering Strategy
                    </label>
                    <select
                      className="nodrag nowheel w-full bg-slate-950/90 border border-violet-500/30 rounded-xl px-2.5 py-1.5 text-violet-100 text-[10.5px] focus:outline-none focus:border-violet-400 disabled:opacity-50"
                      value={nodeData.sipAction || 'luna_live_answer'}
                      onChange={(e) => handleChange('sipAction', e.target.value)}
                    >
                      <option value="luna_live_answer">🎙️ Real-time 2-Way Voice (Luna Neural Voice Bridge)</option>
                      <option value="voice_note_reply">📱 Smart Voice Note Response & WhatsApp Alert</option>
                      <option value="live_web_handover">🌐 3D Avatar WebRTC Live Call Handover</option>
                    </select>
                  </div>

                  {/* SIP Server & Port Grid */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-[9px] font-mono uppercase text-slate-400 font-bold flex items-center gap-1">
                          <Server size={9} className="text-violet-400" /> SIP Server
                        </label>
                        <button
                          type="button"
                          onClick={() => {
                            if (typeof navigator !== 'undefined' && navigator.clipboard) {
                              navigator.clipboard.writeText(sipServerHost);
                              setCopiedSipHost(true);
                              setTimeout(() => setCopiedSipHost(false), 2000);
                            }
                          }}
                          className="nodrag text-[8.5px] font-mono text-violet-400 hover:text-violet-300 font-bold flex items-center gap-0.5 cursor-pointer"
                        >
                          {copiedSipHost ? <Check size={9} className="text-emerald-400" /> : <Copy size={9} />}
                          <span>{copiedSipHost ? 'COPIED' : 'COPY'}</span>
                        </button>
                      </div>
                      <input
                        type="text"
                        className="nodrag nowheel w-full bg-slate-950/80 border border-violet-500/20 rounded-xl px-2.5 py-1 text-violet-200 text-[10px] font-mono focus:outline-none focus:border-violet-400"
                        value={nodeData.sipServerHost || sipServerHost}
                        onChange={(e) => handleChange('sipServerHost', e.target.value)}
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-[9px] font-mono uppercase text-slate-400 font-bold flex items-center gap-1">
                          <Radio size={9} className="text-violet-400" /> SIP Port
                        </label>
                        <button
                          type="button"
                          onClick={() => {
                            if (typeof navigator !== 'undefined' && navigator.clipboard) {
                              navigator.clipboard.writeText(String(sipPort));
                              setCopiedSipPort(true);
                              setTimeout(() => setCopiedSipPort(false), 2000);
                            }
                          }}
                          className="nodrag text-[8.5px] font-mono text-violet-400 hover:text-violet-300 font-bold flex items-center gap-0.5 cursor-pointer"
                        >
                          {copiedSipPort ? <Check size={9} className="text-emerald-400" /> : <Copy size={9} />}
                          <span>{copiedSipPort ? 'COPIED' : 'COPY'}</span>
                        </button>
                      </div>
                      <input
                        type="number"
                        className="nodrag nowheel w-full bg-slate-950/80 border border-violet-500/20 rounded-xl px-2.5 py-1 text-violet-200 text-[10px] font-mono focus:outline-none focus:border-violet-400"
                        value={nodeData.sipPort || sipPort}
                        onChange={(e) => handleChange('sipPort', Number(e.target.value))}
                      />
                    </div>
                  </div>

                  {/* App ID */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5">
                        <label className="text-[9px] font-mono uppercase text-slate-400 font-bold flex items-center gap-1">
                          <Key size={9} className="text-violet-400" /> Meta App ID
                        </label>
                        {isCustomIsolatedApp ? (
                          <span className="text-[7.5px] font-mono px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30 font-bold">
                            ISOLATED CUSTOM APP
                          </span>
                        ) : (
                          <span className="text-[7.5px] font-mono px-1.5 py-0.2 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-bold">
                            AUTO DEFAULT (KANGAVENA)
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        {isCustomIsolatedApp && (
                          <button
                            type="button"
                            onClick={() => handleChange('sipAppId', KANGAVENA_OFFICIAL_META_APP_ID)}
                            className="nodrag text-[8px] font-mono text-amber-300 hover:text-amber-200 underline cursor-pointer"
                          >
                            Reset to Default
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            if (typeof navigator !== 'undefined' && navigator.clipboard) {
                              navigator.clipboard.writeText(sipAppId);
                              setCopiedSipAppId(true);
                              setTimeout(() => setCopiedSipAppId(false), 2000);
                            }
                          }}
                          className="nodrag text-[8.5px] font-mono text-violet-400 hover:text-violet-300 font-bold flex items-center gap-0.5 cursor-pointer"
                        >
                          {copiedSipAppId ? <Check size={9} className="text-emerald-400" /> : <Copy size={9} />}
                          <span>{copiedSipAppId ? 'COPIED' : 'COPY'}</span>
                        </button>
                      </div>
                    </div>
                    <input
                      type="text"
                      className="nodrag nowheel w-full bg-slate-950/80 border border-violet-500/20 rounded-xl px-2.5 py-1 text-violet-200 text-[10px] font-mono focus:outline-none focus:border-violet-400"
                      placeholder={`Default: ${KANGAVENA_OFFICIAL_META_APP_ID}`}
                      value={sipAppId}
                      onChange={(e) => handleChange('sipAppId', e.target.value.trim())}
                    />
                    <p className="text-[7.5px] text-slate-500 mt-0.5 font-sans">
                      {isCustomIsolatedApp 
                        ? 'Using isolated custom Meta App ID for this tenant.' 
                        : 'Auto-configured to Kangavena Master Tech Provider App. You can type an isolated App ID if desired.'}
                    </p>
                  </div>

                  {/* Request URI User Parameters */}
                  <div>
                    <label className="block text-[9px] font-mono uppercase text-slate-400 font-bold mb-1">
                      Request URI user parameters
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="relative">
                        <input
                          type="text"
                          className="nodrag nowheel w-full bg-slate-950/80 border border-violet-500/20 rounded-xl px-2.5 py-1 text-violet-200 text-[10px] font-mono focus:outline-none focus:border-violet-400"
                          placeholder="Name (e.g. tenant_id)"
                          value={nodeData.sipCustomParamName || sipParamName}
                          onChange={(e) => handleChange('sipCustomParamName', e.target.value)}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            if (typeof navigator !== 'undefined' && navigator.clipboard) {
                              navigator.clipboard.writeText(nodeData.sipCustomParamName || sipParamName);
                              setCopiedSipParamName(true);
                              setTimeout(() => setCopiedSipParamName(false), 2000);
                            }
                          }}
                          className="nodrag absolute right-1.5 top-1.5 text-violet-400 hover:text-violet-300 cursor-pointer"
                        >
                          {copiedSipParamName ? <Check size={10} className="text-emerald-400" /> : <Copy size={10} />}
                        </button>
                      </div>
                      <div className="relative">
                        <input
                          type="text"
                          className="nodrag nowheel w-full bg-slate-950/80 border border-violet-500/20 rounded-xl px-2.5 py-1 text-violet-200 text-[10px] font-mono focus:outline-none focus:border-violet-400"
                          placeholder="Value (e.g. 11)"
                          value={nodeData.sipCustomParamValue || sipParamValue}
                          onChange={(e) => handleChange('sipCustomParamValue', e.target.value)}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            if (typeof navigator !== 'undefined' && navigator.clipboard) {
                              navigator.clipboard.writeText(nodeData.sipCustomParamValue || sipParamValue);
                              setCopiedSipParamValue(true);
                              setTimeout(() => setCopiedSipParamValue(false), 2000);
                            }
                          }}
                          className="nodrag absolute right-1.5 top-1.5 text-violet-400 hover:text-violet-300 cursor-pointer"
                        >
                          {copiedSipParamValue ? <Check size={10} className="text-emerald-400" /> : <Copy size={10} />}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Helper Tip */}
                  <div className="p-2 rounded-xl bg-violet-950/50 border border-violet-500/30 text-[8.5px] text-violet-300/90 leading-relaxed font-sans space-y-1">
                    <div>💡 <strong>How to complete Meta setup:</strong> In WhatsApp Manager ➜ Phone numbers ➜ <strong>Call settings</strong> ➜ Turn ON <strong>&quot;Allow voice calls&quot;</strong> ➜ Click <strong>&quot;Set up&quot;</strong> under <em>Use Session Initiation Protocol (SIP)</em> ➜ Paste the Server (<code>wa.kangavena.com</code>), Port (<code>5061</code>), App ID, and Parameter values above ➜ Click <strong>Save</strong>.</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* ═══════════════════════════════════════════════════════════════ */
          /* OPTION 2: UNOFFICIAL EVOLUTION API CONFIGURATION PANEL          */
          /* ═══════════════════════════════════════════════════════════════ */
          <div className="space-y-3.5">
            {/* Instance Name Field */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                  <Smartphone size={12} className="text-emerald-400" /> Instance Identifier
                </label>
                {isTenantView && (
                  <span className="text-[8.5px] font-mono text-emerald-400 font-bold bg-emerald-500/10 border border-emerald-500/30 px-1.5 py-0.5 rounded">
                    ⚡ Auto-Assigned ({defaultTenantInstance || `tenet_${tenant?.id || 'id'}`})
                  </span>
                )}
              </div>
              <input
                type="text"
                disabled={evoStatus === 'LOADING_QR' || isTenantView}
                className="nodrag nowheel w-full bg-slate-900/90 border border-white/10 rounded-xl px-3 py-2 text-slate-100 text-xs focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400/30 transition-all placeholder:text-slate-600 font-mono disabled:opacity-75 disabled:cursor-not-allowed"
                placeholder={isTenantView ? 'Auto-managed tenant instance' : 'e.g. sales-whatsapp'}
                value={isTenantView ? (defaultTenantInstance || instanceName) : instanceName}
                onChange={(e) => {
                  setInstanceName(e.target.value);
                  handleChange('instanceName', e.target.value);
                }}
              />
            </div>

            {/* ── STATE A: DISCONNECTED (Show Connect & Scan QR Button) ── */}
            {evoStatus === 'DISCONNECTED' && (
              <button
                type="button"
                disabled={!isTenantView && !instanceName.trim()}
                onClick={handleConnectWhatsApp}
                className="nodrag w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 via-green-600 to-teal-500 hover:from-emerald-500 hover:to-teal-400 text-white text-xs font-black uppercase tracking-wider shadow-lg shadow-emerald-500/25 transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                <QrCode size={14} /> Connect WhatsApp (Scan QR)
              </button>
            )}

            {/* ── STATE B: LOADING QR CODE ── */}
            {evoStatus === 'LOADING_QR' && (
              <div className="flex flex-col items-center justify-center gap-2 p-4 rounded-xl bg-slate-900/90 border border-amber-500/30 text-center animate-pulse">
                <Loader2 size={24} className="text-amber-400 animate-spin" />
                <p className="text-[11px] font-bold text-amber-300">Generating Evolution QR Code...</p>
                <p className="text-[9px] text-slate-400 font-mono">Requesting pairing token from server</p>
              </div>
            )}

            {/* ── STATE C: SCAN READY (Show Live QR Code) ── */}
            {evoStatus === 'SCAN_READY' && qrCode && (
              <div className="flex flex-col items-center gap-2.5 p-3 rounded-xl bg-black/60 border border-sky-500/40 shadow-inner">
                <div className="flex items-center gap-1.5 text-sky-300 text-[10px] font-bold">
                  <QrCode size={13} className="animate-pulse" />
                  <span>Scan QR Code with WhatsApp Mobile</span>
                </div>

                <div className="bg-white p-2.5 rounded-xl shadow-2xl">
                  <img
                    src={qrCode.startsWith('data:') ? qrCode : `data:image/png;base64,${qrCode}`}
                    alt="WhatsApp QR Code"
                    className="w-44 h-44 object-contain block"
                  />
                </div>

                <div className="text-[9px] text-slate-300 space-y-0.5 text-center font-sans">
                  <p className="font-bold text-sky-200">1. Open WhatsApp on phone &rarr; Settings</p>
                  <p>2. Tap <span className="font-semibold text-white">Linked Devices</span> &rarr; <span className="font-semibold text-emerald-300">Link a Device</span></p>
                  <p>3. Point your camera at this QR code</p>
                </div>

                <div className="flex items-center gap-2 w-full pt-1">
                  <button
                    type="button"
                    onClick={handleConnectWhatsApp}
                    className="nodrag flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-500/20 hover:bg-sky-500/30 text-sky-300 border border-sky-500/40 text-[10px] font-bold transition cursor-pointer"
                  >
                    <RefreshCw size={11} /> Refresh QR
                  </button>
                  <button
                    type="button"
                    disabled={isDisconnecting}
                    onClick={handleDisconnectWhatsApp}
                    className="nodrag px-3 py-1.5 rounded-lg bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 border border-rose-500/40 text-[10px] font-bold transition cursor-pointer"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* ── STATE D: CONNECTED STATE BANNER & RE-LINK OPTION ── */}
            {evoStatus === 'CONNECTED' && (
              <div className="flex flex-col items-center gap-2.5 p-3.5 rounded-xl bg-emerald-950/40 border border-emerald-500/40 shadow-inner text-center">
                <div className="w-10 h-10 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400 shadow-[0_0_20px_rgba(16,185,129,0.4)]">
                  <CheckCircle2 size={22} />
                </div>
                <div>
                  <p className="text-xs font-bold text-emerald-300">WhatsApp Linked & Connected</p>
                  {linkedPhone ? (
                    <p className="text-[12px] text-emerald-400 font-mono font-bold mt-0.5 tracking-wide">{linkedPhone}</p>
                  ) : (
                    <p className="text-[10px] text-emerald-300/70 font-mono">Autonomous AI Co-Pilot Active</p>
                  )}
                  <p className="text-[9px] text-slate-400 mt-1">Customer chats are answered 24/7 by Luna AI</p>
                </div>

                <button
                  type="button"
                  disabled={isDisconnecting}
                  onClick={handleDisconnectWhatsApp}
                  className="nodrag w-full mt-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-rose-500/15 hover:bg-rose-500/25 text-rose-300 border border-rose-500/30 text-[10px] font-bold transition-all cursor-pointer disabled:opacity-50"
                >
                  {isDisconnecting ? (
                    <><Loader2 size={12} className="animate-spin" /> Disconnecting...</>
                  ) : (
                    <><LogOut size={12} /> Disconnect / Link Different WhatsApp</>
                  )}
                </button>
              </div>
            )}

            {/* Error Alert Display */}
            {error && (
              <div className="p-2.5 rounded-xl bg-rose-950/50 border border-rose-500/40 text-[10px] text-rose-300 flex items-start gap-2">
                <AlertCircle size={13} className="text-rose-400 shrink-0 mt-0.5" />
                <div className="flex-1 break-words font-mono">{error}</div>
                <button
                  type="button"
                  onClick={() => setError(null)}
                  className="text-rose-400 hover:text-rose-200 cursor-pointer"
                >
                  <XCircle size={12} />
                </button>
              </div>
            )}

            {/* Check Status Action Button */}
            {evoStatus !== 'LOADING_QR' && evoStatus !== 'SCAN_READY' && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={isCheckingStatus}
                  onClick={() => checkStatus(false)}
                  className="nodrag flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-slate-900/90 hover:bg-slate-800 text-slate-300 border border-white/10 text-[10px] font-bold transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <RefreshCw size={11} className={isCheckingStatus ? 'animate-spin text-emerald-400' : ''} />
                  Status
                </button>
                {evoStatus === 'CONNECTED' && triggerPulse && (
                  <button
                    type="button"
                    onClick={() => triggerPulse(['in-core', id, 'out-whatsapp-instance'])}
                    className="nodrag flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 text-[10px] font-bold transition-all cursor-pointer"
                  >
                    <Activity size={11} className="animate-pulse" />
                    Simulate Pulse
                  </button>
                )}
              </div>
            )}

            {/* ── ANTI-BAN HUMAN BEHAVIOR EMULATION SUITE ── */}
            <div className="p-3 rounded-2xl bg-gradient-to-br from-emerald-950/40 via-slate-900/90 to-slate-950/90 border border-emerald-500/30 space-y-2.5 shadow-inner">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <ShieldCheck size={14} className="text-emerald-400" />
                  <span className="text-[10px] font-black uppercase tracking-wider text-emerald-300">
                    Meta Anti-Ban Protection
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleChange('antiBanEnabled', nodeData.antiBanEnabled === false ? true : false)}
                  className={`nodrag px-2 py-0.5 rounded-md text-[9px] font-mono font-bold border transition-all cursor-pointer ${
                    nodeData.antiBanEnabled !== false
                      ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40 shadow-[0_0_10px_rgba(16,185,129,0.2)]'
                      : 'bg-slate-800 text-slate-400 border-slate-700'
                  }`}
                >
                  {nodeData.antiBanEnabled !== false ? '🛡️ GUARD ON' : 'OFF'}
                </button>
              </div>

              {nodeData.antiBanEnabled !== false && (
                <div className="space-y-2 pt-1 border-t border-emerald-500/10">
                  {/* Human Reading Delay Options */}
                  <div>
                    <div className="flex items-center justify-between text-[9px] font-mono text-slate-400 mb-1">
                      <span>Human Read/Think Gap:</span>
                      <span className="text-emerald-300 font-bold">
                        {(nodeData.antiBanReadDelay ?? 600)}ms
                      </span>
                    </div>
                    <div className="grid grid-cols-4 gap-1">
                      {[
                        { label: '300ms', val: 300 },
                        { label: '600ms (Rec)', val: 600 },
                        { label: '1000ms', val: 1000 },
                        { label: '1500ms', val: 1500 },
                      ].map((item) => (
                        <button
                          key={item.val}
                          type="button"
                          onClick={() => handleChange('antiBanReadDelay', item.val)}
                          className={`nodrag py-1 rounded-lg text-[8.5px] font-mono font-bold border transition-all cursor-pointer text-center ${
                            (nodeData.antiBanReadDelay ?? 600) === item.val
                              ? 'bg-emerald-500/30 text-emerald-200 border-emerald-400 shadow-sm'
                              : 'bg-slate-900/80 text-slate-400 border-white/5 hover:border-white/10'
                          }`}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Typing Emulation Speed */}
                  <div>
                    <div className="flex items-center justify-between text-[9px] font-mono text-slate-400 mb-1">
                      <span>Typing Speed Profile:</span>
                      <span className="text-emerald-300 font-bold capitalize">
                        {nodeData.antiBanTypingSpeed || 'natural'}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-1">
                      {[
                        { label: '⚡ Fast (15ms)', val: 'fast' },
                        { label: '🟢 Natural (25ms)', val: 'natural' },
                        { label: '☕ Relaxed (35ms)', val: 'relaxed' },
                      ].map((item) => (
                        <button
                          key={item.val}
                          type="button"
                          onClick={() => handleChange('antiBanTypingSpeed', item.val)}
                          className={`nodrag py-1 rounded-lg text-[8.5px] font-mono font-bold border transition-all cursor-pointer text-center ${
                            (nodeData.antiBanTypingSpeed || 'natural') === item.val
                              ? 'bg-emerald-500/30 text-emerald-200 border-emerald-400 shadow-sm'
                              : 'bg-slate-900/80 text-slate-400 border-white/5 hover:border-white/10'
                          }`}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <p className="text-[8px] text-emerald-400/80 font-mono flex items-center gap-1 pt-0.5">
                    <Check size={9} /> Emulates realistic human typing & suppresses bot fingerprints.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* SHARED CONTROLS (AI AUTO-REPLY, FORMATTING, DISPLAY NAME)        */}
        {/* ═══════════════════════════════════════════════════════════════ */}

        {/* ── AI Auto-Reply Master Switch (Instant 1-Click Pause/Resume) ── */}
        <div className={`p-3 rounded-2xl border flex items-center justify-between shadow-md transition-all ${
          nodeData.autoReply !== false
            ? 'bg-slate-900/90 border-emerald-500/30 shadow-[0_0_15px_rgba(16,185,129,0.1)]'
            : 'bg-slate-900/90 border-rose-500/30 shadow-[0_0_15px_rgba(244,63,94,0.1)]'
        }`}>
          <div className="flex items-center gap-2">
            <Bot size={16} className={nodeData.autoReply !== false ? 'text-emerald-400 animate-pulse' : 'text-slate-500'} />
            <div>
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-200 block">
                AI Auto-Reply Switch
              </span>
              <span className="text-[9px] font-mono text-slate-400">
                {nodeData.autoReply !== false ? '🟢 Responding 24/7' : '⏸️ Paused (Muted)'}
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              const nextState = nodeData.autoReply === false ? true : false;
              handleChange('autoReply', nextState);
              // Direct sync to settings API
              const headers = getAuthHeaders();
              fetch('/api/tenet/whatsapp/settings', {
                method: 'PUT',
                headers,
                body: JSON.stringify({ nodeId: id, autoReply: nextState }),
              }).catch((e) => console.warn('[WhatsAppInstanceNode] settings sync error:', e));
            }}
            className={`nodrag px-3 py-1.5 rounded-xl text-[10px] font-mono font-black border transition-all cursor-pointer flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 ${
              nodeData.autoReply !== false
                ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/30 hover:border-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.3)]'
                : 'bg-rose-500/20 text-rose-300 border-rose-500/40 hover:bg-rose-500/30 hover:border-rose-400 shadow-[0_0_15px_rgba(244,63,94,0.3)]'
            }`}
            title={nodeData.autoReply !== false ? 'Click to Pause WhatsApp Auto-Reply' : 'Click to Enable WhatsApp Auto-Reply'}
          >
            <span className={`w-2 h-2 rounded-full ${nodeData.autoReply !== false ? 'bg-emerald-400 animate-pulse' : 'bg-rose-400'}`} />
            <span>{nodeData.autoReply !== false ? 'ENABLED' : 'PAUSED'}</span>
          </button>
        </div>

        {/* ── Personal WhatsApp Protection & Owner Manual Reply Takeover ── */}
        <div className={`p-3 rounded-2xl border transition-all ${
          nodeData.isPersonalWhatsApp
            ? 'bg-gradient-to-br from-purple-950/40 via-slate-900 to-slate-950 border-purple-500/40 shadow-[0_0_20px_rgba(168,85,247,0.15)]'
            : 'bg-slate-900/90 border-white/10'
        }`}>
          {/* Header & Toggle */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} className={nodeData.isPersonalWhatsApp ? 'text-purple-400 animate-pulse' : 'text-slate-500'} />
              <div>
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-200 block">
                  Personal WhatsApp Mode
                </span>
                <span className="text-[9px] font-mono text-slate-400">
                  {nodeData.isPersonalWhatsApp ? '🛡️ Friends & Family Filter Active' : 'Off (All chats answered)'}
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                const nextVal = !nodeData.isPersonalWhatsApp;
                handleChange('isPersonalWhatsApp', nextVal);
              }}
              className={`nodrag px-2.5 py-1 rounded-xl text-[9px] font-mono font-black border transition-all flex items-center gap-1.5 cursor-pointer ${
                nodeData.isPersonalWhatsApp
                  ? 'bg-purple-500/20 text-purple-300 border-purple-500/40 shadow-sm'
                  : 'bg-slate-800 text-slate-400 border-slate-700 hover:text-white'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${nodeData.isPersonalWhatsApp ? 'bg-purple-400 animate-pulse' : 'bg-slate-600'}`} />
              <span>{nodeData.isPersonalWhatsApp ? 'PROTECTED' : 'DISABLED'}</span>
            </button>
          </div>

          {/* Explanation badge based on mode */}
          <div className="pt-1 pb-2 border-t border-white/5 text-[8.5px] font-mono leading-relaxed">
            {nodeData.isPersonalWhatsApp ? (
              <p className="text-purple-300/90">
                🔒 <strong className="text-purple-200">Personal Mode:</strong> AI filters incoming contacts. Owner manual messages <span className="text-purple-300 font-bold underline">permanently mute</span> Luna for that contact.
              </p>
            ) : (
              <p className="text-emerald-400/90">
                ⚡ <strong className="text-emerald-300">Business Mode:</strong> Luna answers all customers 24/7. When owner replies manually, Luna <span className="text-emerald-300 font-bold underline">pauses temporarily</span> for the duration below.
              </p>
            )}
          </div>

          {/* Owner Manual Reply Snooze / Mute Duration Selector (ALWAYS ACTIVE) */}
          <div className="space-y-1.5 pt-1.5 border-t border-white/5">
            <div className="flex items-center justify-between text-[9px] font-mono text-slate-300">
              <span className="flex items-center gap-1 font-bold">
                <Clock size={11} className={nodeData.isPersonalWhatsApp ? 'text-purple-400' : 'text-emerald-400'} />
                Owner Reply Takeover Pause:
              </span>
              <span className={`font-black ${nodeData.isPersonalWhatsApp ? 'text-purple-300' : 'text-emerald-400'}`}>
                {nodeData.isPersonalWhatsApp
                  ? 'Permanent (Active)'
                  : (nodeData.ownerPauseMinutes ?? 30) >= 99999
                  ? 'Permanent'
                  : `${(nodeData.ownerPauseMinutes ?? 30)} min`}
              </span>
            </div>

            {/* Pill Selectors */}
            <div className="grid grid-cols-4 gap-1">
              {[
                { label: '15 min', val: 15 },
                { label: '30m (Rec)', val: 30 },
                { label: '1 hour', val: 60 },
                { label: '2 hours', val: 120 },
                { label: '4 hours', val: 240 },
                { label: '12 hours', val: 720 },
                { label: '24 hours', val: 1440 },
                { label: 'Permanent', val: 99999 },
              ].map((item) => (
                <button
                  key={item.val}
                  type="button"
                  onClick={() => handleChange('ownerPauseMinutes', item.val)}
                  className={`nodrag py-1 rounded-lg text-[8.5px] font-mono font-bold border transition-all cursor-pointer text-center ${
                    (nodeData.ownerPauseMinutes ?? 30) === item.val
                      ? (nodeData.isPersonalWhatsApp
                          ? 'bg-purple-500/30 text-purple-200 border-purple-400 shadow-sm'
                          : 'bg-emerald-500/30 text-emerald-200 border-emerald-400 shadow-sm')
                      : 'bg-slate-900/80 text-slate-400 border-white/5 hover:border-white/10 hover:text-white'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {/* Custom Minutes Input */}
            <div className="flex items-center gap-2 pt-1">
              <span className="text-[8.5px] font-mono text-slate-400 shrink-0">Custom Pause:</span>
              <div className="relative flex-1">
                <input
                  type="number"
                  min="1"
                  max="99999"
                  value={nodeData.ownerPauseMinutes ?? 30}
                  onChange={(e) => {
                    const val = Math.max(1, parseInt(e.target.value) || 30);
                    handleChange('ownerPauseMinutes', val);
                  }}
                  className="nodrag w-full bg-slate-950/90 border border-white/10 rounded-lg px-2 py-0.5 text-[9px] font-mono text-slate-200 focus:outline-none focus:border-emerald-500/50"
                  placeholder="Minutes"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[8px] font-mono text-slate-500 pointer-events-none">
                  min
                </span>
              </div>
            </div>

            {nodeData.isPersonalWhatsApp && (
              <div className="pt-1.5">
                <button
                  type="button"
                  onClick={() => openPortalData?.('whatsapp_instance', id)}
                  className="nodrag w-full py-1 px-2 rounded-lg bg-purple-950/40 hover:bg-purple-900/40 border border-purple-500/30 text-[9px] font-mono text-purple-300 font-bold flex items-center justify-center gap-1.5 transition cursor-pointer"
                >
                  <Shield size={10} /> View Ignored Contacts Studio
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Linked Phone Number Input/Display */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-[10px] font-black uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
              <Phone size={12} className={apiMode === 'official' ? 'text-sky-400' : 'text-emerald-400'} /> 
              <span>{apiMode === 'official' ? 'Registered WhatsApp Phone' : 'Linked WhatsApp Phone'}</span>
            </label>
            {autoDetectedRegisteredPhone && linkedPhone !== autoDetectedRegisteredPhone && (
              <button
                type="button"
                onClick={() => {
                  setLinkedPhone(autoDetectedRegisteredPhone);
                  handleChange('phoneNumber', autoDetectedRegisteredPhone);
                }}
                className="nodrag text-[8px] font-mono text-sky-400 hover:text-sky-300 underline font-bold cursor-pointer"
              >
                Auto-Fill Registered ({autoDetectedRegisteredPhone})
              </button>
            )}
          </div>
          <input
            type="text"
            className="nodrag nowheel w-full bg-slate-900/90 border border-white/10 rounded-xl px-3 py-2 text-slate-100 text-xs focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400/30 transition-all placeholder:text-slate-600 font-mono disabled:opacity-50 disabled:cursor-not-allowed"
            placeholder={autoDetectedRegisteredPhone || "+94 77 123 4567"}
            value={linkedPhone || autoDetectedRegisteredPhone || ''}
            onChange={(e) => {
              setLinkedPhone(e.target.value);
              handleChange('phoneNumber', e.target.value);
            }}
          />
          {autoDetectedRegisteredPhone && (
            <p className="text-[8px] text-emerald-400/80 mt-0.5 font-mono">
              ✓ Auto-detected from Tenant Profile: {autoDetectedRegisteredPhone}
            </p>
          )}
        </div>

        {/* WhatsApp Formatting & Sales Copy Style */}
        <div>
          <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1.5">
            <Sparkles size={12} className={apiMode === 'official' ? 'text-sky-400' : 'text-emerald-400'} /> WhatsApp Response Format & Style
          </label>
          <select
            className="nodrag w-full bg-slate-900/90 border border-white/10 rounded-xl px-3 py-2 text-slate-100 text-xs disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400/30 transition-all cursor-pointer font-medium"
            value={nodeData.whatsappFormattingStyle || 'sales_promo'}
            onChange={(e) => handleChange('whatsappFormattingStyle', e.target.value)}
          >
            <option value="sales_promo" className="bg-slate-900 text-slate-100">🔥 High-Converting Sales Copy & Promo (Prices, Emojis, Bullet Points)</option>
            <option value="business_corporate" className="bg-slate-900 text-slate-100">🏢 Professional Corporate & Clean Executive</option>
            <option value="friendly_concierge" className="bg-slate-900 text-slate-100">💬 Friendly Conversational & Warm Concierge</option>
            <option value="technical_expert" className="bg-slate-900 text-slate-100">🔬 Technical & Deep Specification Details</option>
            <option value="direct_short" className="bg-slate-900 text-slate-100">⚡ Direct, Fast & Ultra-Short Bullet Points</option>
          </select>
        </div>

        {/* Display Name */}
        <div>
          <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5">
            Bot Display Name
          </label>
          <input
            type="text"
            className="nodrag nowheel w-full bg-slate-900/90 border border-white/10 rounded-xl px-3 py-2 text-slate-100 text-xs focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400/30 transition-all placeholder:text-slate-600 font-sans disabled:opacity-50 disabled:cursor-not-allowed"
            placeholder="e.g. KangaVena Sales Bot"
            value={nodeData.displayName || ''}
            onChange={(e) => handleChange('displayName', e.target.value)}
          />
        </div>

        {/* Launch WhatsApp Web Console with Orders & Leads Highlight */}
        {openPortalData && (
          <div className="p-3 rounded-2xl bg-gradient-to-r from-emerald-950/60 via-slate-900/90 to-teal-950/60 border border-emerald-500/30 space-y-2 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center text-base shadow-sm">
                  🛍️
                </div>
                <div>
                  <p className="text-[11px] font-bold text-emerald-300">Captured Orders & Leads Hub</p>
                  <p className="text-[9px] text-slate-400">Live WhatsApp chat console with filter tabs</p>
                </div>
              </div>
              <span className="text-[9px] font-mono font-bold px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                ACTIVE
              </span>
            </div>
            <button
              type="button"
              onClick={() => openPortalData('whatsapp_web', id)}
              className="nodrag w-full py-2 px-4 rounded-xl bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600 hover:from-emerald-400 hover:to-teal-400 text-slate-950 font-black text-xs flex items-center justify-center gap-2 shadow-xl shadow-emerald-500/25 cursor-pointer active:scale-95 transition-all"
              title="Launch authentic WhatsApp Web & Mobile Console for live customer chats & human takeover"
            >
              <Smartphone size={14} />
              <span>Open WhatsApp Web Console</span>
              <ArrowRight size={13} className="text-slate-950" />
            </button>

            <div className="flex items-center gap-2 pt-1 border-t border-emerald-500/20">
              <button
                type="button"
                onClick={() => setShowDeleteChatsModal(true)}
                className="nodrag flex-1 py-1.5 px-2.5 rounded-xl bg-rose-950/40 hover:bg-rose-900/60 border border-rose-500/30 text-rose-300 text-[10px] font-bold flex items-center justify-center gap-1.5 transition cursor-pointer active:scale-95"
                title="Delete all stored WhatsApp chat history & reset Luna context"
              >
                <Trash2 size={11} className="text-rose-400" />
                <span>Delete All Chats</span>
              </button>
              <button
                type="button"
                onClick={() => openPortalData('whatsapp_web', id)}
                className="nodrag py-1.5 px-2.5 rounded-xl bg-emerald-950/40 hover:bg-emerald-900/60 border border-emerald-500/30 text-emerald-300 text-[10px] font-bold flex items-center justify-center gap-1 transition cursor-pointer"
                title="Open WhatsApp Web to delete individual conversations"
              >
                <span>Individual Chats →</span>
              </button>
            </div>
          </div>
        )}

        {/* Clear Chat Status Toast */}
        {clearChatStatus && (
          <div className="p-2 rounded-xl bg-emerald-950/90 border border-emerald-500/50 text-emerald-300 text-[11px] font-semibold animate-in fade-in">
            {clearChatStatus}
          </div>
        )}

        {/* Zero-Trust Security Footnote */}
        <div className="flex items-center gap-2 pt-1 border-t border-white/5">
          <Shield size={12} className={apiMode === 'official' ? 'text-sky-400' : 'text-emerald-400'} />
          <span className="text-[10px] text-slate-400 font-mono">
            {apiMode === 'official' ? 'Meta Cloud API TLS encrypted & webhook token authenticated' : 'Evolution API isolated server-side with zero data leak'}
          </span>
        </div>
      </div>
      )}

      {/* ── Clear All WhatsApp Chats Confirmation Modal ── */}
      {showDeleteChatsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4 animate-in fade-in duration-150">
          <div className="w-full max-w-md bg-slate-900 border border-rose-500/40 rounded-2xl shadow-2xl p-5 space-y-4 text-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-rose-500/20 border border-rose-500/50 flex items-center justify-center text-rose-400 shrink-0">
                <Trash2 size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="text-sm font-bold text-white">Delete WhatsApp Chat History?</h4>
                <p className="text-[11px] text-slate-400">Node: WhatsApp Instance Hub</p>
              </div>
            </div>

            <div className="bg-rose-950/30 border border-rose-500/30 rounded-xl p-3 space-y-1.5 text-xs text-rose-200">
              <p className="font-bold flex items-center gap-1 text-rose-300">
                <span>⚠️ Clear All WhatsApp Conversations</span>
              </p>
              <p className="text-slate-300 text-[11px] leading-relaxed">
                This will permanently delete all customer WhatsApp conversation records from the database and wipe Luna AI&apos;s active conversation memory context.
              </p>
            </div>

            <div className="flex items-center justify-between gap-2 pt-2 border-t border-white/10">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteChatsModal(false);
                  openPortalData?.('whatsapp_web', id);
                }}
                className="text-[11px] text-emerald-400 hover:text-emerald-300 underline font-semibold"
              >
                Manage individual chats →
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={isClearingChats}
                  onClick={() => setShowDeleteChatsModal(false)}
                  className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-slate-300 transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={isClearingChats}
                  onClick={handleClearAllWhatsAppChats}
                  className="px-3.5 py-1.5 rounded-xl bg-rose-600 hover:bg-rose-500 text-xs font-bold text-white transition flex items-center gap-1.5 cursor-pointer shadow-lg shadow-rose-600/30 disabled:opacity-50"
                >
                  {isClearingChats ? (
                    <>
                      <Loader2 size={13} className="animate-spin" />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 size={13} />
                      <span>Yes, Delete All</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSimulatorModal && (
        <KangavenaWhatsAppSimulatorModal
          isOpen={showSimulatorModal}
          onClose={() => setShowSimulatorModal(false)}
          token={authToken}
          tenant={tenant}
          agentName={typeof tenant?.customAvatarName === 'string' ? tenant.customAvatarName : undefined}
        />
      )}

      <Handle
        type="source"
        position={Position.Bottom}
        id="out-bottom"
        className={`!w-3.5 !h-3.5 !border-2 !border-slate-950 !-bottom-2 ${
          apiMode === 'official' ? '!bg-sky-400' : '!bg-emerald-400'
        }`}
      />
    </div>
  );
});

WhatsAppInstanceNode.displayName = 'WhatsAppInstanceNode';
export default WhatsAppInstanceNode;
